import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card, Button, Table, Tag, Space, Typography, message, Modal, Form, Input,
  Select, Empty, Popconfirm, Badge, InputNumber, Alert,
  AutoComplete, Row, Col, Checkbox, Divider
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ThunderboltOutlined, UserOutlined,
  CrownOutlined, SyncOutlined, CheckCircleOutlined, HistoryOutlined,
  EditOutlined, SaveOutlined, GiftOutlined, CalculatorOutlined,
  InboxOutlined, ReloadOutlined, MinusOutlined, DollarOutlined,
  TeamOutlined
} from '@ant-design/icons';
// 注：Row/Col/CalculatorOutlined 仍被废弃的分配代码保留引用，不删除避免回滚困难
// eslint-disable-next-line no-unused-vars
const _UNUSED_IMPORTS = { Row, Col, CalculatorOutlined };

const { Text, Title } = Typography;
const { Option } = Select;

const API_BASE_URL = '';

// FastAPI 422 校验错误的 detail 是数组/对象而非字符串，直接传给 messageApi.error 会因渲染非法 React 子节点导致整个页面白屏
const getErrorText = (data, fallback) => {
  const d = data?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d)) {
    const text = d.map(e => (typeof e === 'string' ? e : e?.msg || JSON.stringify(e))).join('; ');
    return text || fallback;
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return fallback;
};

// Boss Rush 参与 Boss 选项（多选）
const BOSS_RUSH_BOSSES = [
  { value: 'Berga-R1', label: 'Berga-R1', color: '#fa8c16' },
  { value: 'Berga-R2', label: 'Berga-R2', color: '#fa541c' },
  { value: 'Dunas1-R1', label: 'Dunas1-R1', color: '#13c2c2' },
  { value: 'Dunas1-R2', label: 'Dunas1-R2', color: '#08979c' },
  { value: 'Nib-R1', label: 'Nib-R1', color: '#722ed1' },
  { value: 'Nib-R2', label: 'Nib-R2', color: '#531dab' },
  { value: 'Nameless-R1', label: 'Nameless-R1', color: '#eb2f96' },
  { value: 'Nameless-R2', label: 'Nameless-R2', color: '#c41d7f' },
  { value: 'Dunas2-R1', label: 'Dunas2-R1', color: '#1890ff' },
  { value: 'Dunas2-R2', label: 'Dunas2-R2', color: '#096dd9' },
  { value: 'Core-R1', label: 'Core-R1', color: '#52c41a' },
  { value: 'Core-R2', label: 'Core-R2', color: '#389e0d' },
  { value: 'Bga-R1', label: 'Bga-R1', color: '#f5222d' },
  { value: 'Auf-R1', label: 'Auf-R1', color: '#cf1322' },
  { value: 'Auf-R2', label: 'Auf-R2', color: '#a8071a' },
];

// ==================== 预设小队组件 ====================

// 预设小队管理弹窗
const PresetTeamManageModal = ({ visible, onCancel, onApply, currentUser, isHost, isAdmin }) => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const loadPresets = useCallback(async () => {
    if (!currentUser?.username) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/preset-teams?username=${encodeURIComponent(currentUser.username)}`);
      if (res.ok) {
        const data = await res.json();
        setPresets(data.presets || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentUser?.username]);

  useEffect(() => {
    if (visible) loadPresets();
  }, [visible, loadPresets]);

  const handleDelete = async (presetId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/preset-teams/${presetId}?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        messageApi.success('预设小队已删除');
        loadPresets();
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '删除失败'));
      }
    } catch (e) { messageApi.error('删除失败'); }
  };

  const handleEdit = (preset) => {
    setEditingPreset(preset);
    setEditModalVisible(true);
  };

  const handleCreate = () => {
    setEditingPreset(null);
    setEditModalVisible(true);
  };

  const handleEditSave = async (values) => {
    try {
      if (editingPreset) {
        const res = await fetch(`${API_BASE_URL}/api/preset-teams/${editingPreset.id}?username=${encodeURIComponent(currentUser.username)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values)
        });
        if (res.ok) {
          messageApi.success('预设小队已更新');
          setEditModalVisible(false);
          loadPresets();
        } else {
          const data = await res.json();
          messageApi.error(getErrorText(data, '更新失败'));
        }
      } else {
        const res = await fetch(`${API_BASE_URL}/api/preset-teams?username=${encodeURIComponent(currentUser.username)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values)
        });
        if (res.ok) {
          messageApi.success('预设小队已创建');
          setEditModalVisible(false);
          loadPresets();
        } else {
          const data = await res.json();
          messageApi.error(getErrorText(data, '创建失败'));
        }
      }
    } catch (e) { messageApi.error('操作失败'); }
  };

  const canManage = isHost || isAdmin;

  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name', render: (name) => <Text strong>{name}</Text> },
    { title: '人数', key: 'count', render: (_, record) => <Tag>{(record.members || []).length} 人</Tag> },
    {
      title: '成员', dataIndex: 'members', key: 'members',
      render: (members) => (
        <Space wrap size={[2, 2]}>
          {(members || []).map((m, i) => {
            const bossCount = (m.participatedBosses || []).length;
            return (
              <Tag key={i} icon={<UserOutlined />} style={{ fontSize: 11 }} title={bossCount > 0 ? `参加 ${bossCount} 个 Boss: ${(m.participatedBosses || []).join(', ')}` : '未设置参加的 Boss'}>
                {m.name}{bossCount > 0 ? ` (${bossCount})` : ''}
              </Tag>
            );
          })}
        </Space>
      )
    },
    { title: '创建者', dataIndex: 'createdBy', key: 'createdBy', width: 100 },
    {
      title: '操作', key: 'action', width: 160,
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { onApply(record); onCancel(); }}>
            应用
          </Button>
          {canManage && (record.createdBy === currentUser?.username || isAdmin) && (
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
              <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <>
      {contextHolder}
      <Modal
        title={<><TeamOutlined /> 预设小队管理</>}
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary">预设小队可以快速将一组角色添加到当前 Boss Rush 中</Text>
          {canManage && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>创建预设小队</Button>
          )}
        </div>
        <Table
          dataSource={presets}
          rowKey="id"
          columns={columns}
          loading={loading}
          size="small"
          pagination={false}
          locale={{ emptyText: <Empty description="暂无预设小队" /> }}
        />
      </Modal>

      <PresetTeamEditModal
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onSave={handleEditSave}
        editingPreset={editingPreset}
        currentUser={currentUser}
      />
    </>
  );
};

// 预设小队编辑弹窗（Boss Rush 版 — 只包含角色名、队长、参与 Boss）
const PresetTeamEditModal = ({ visible, onCancel, onSave, editingPreset, currentUser }) => {
  const [form] = Form.useForm();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  // 批量设置：被勾选的成员下标 + 待应用的 Boss 列表
  const [selectedIdxs, setSelectedIdxs] = useState([]);
  const [batchBosses, setBatchBosses] = useState([]);

  useEffect(() => {
    if (visible) {
      setSelectedIdxs([]);
      setBatchBosses([]);
      if (editingPreset) {
        form.setFieldsValue({ name: editingPreset.name });
        setMembers(editingPreset.members || []);
      } else {
        form.resetFields();
        setMembers([]);
      }
    }
  }, [visible, editingPreset, form]);

  const handleAddMember = () => {
    setMembers(prev => [...prev, { name: '', team: 1, participatedBosses: [] }]);
  };

  const handleRemoveMember = (index) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
    // 删除成员后同步清理选中态（并修正后续下标）
    setSelectedIdxs(prev => prev.filter(i => i !== index).map(i => (i > index ? i - 1 : i)));
  };

  const handleMemberChange = (index, field, value) => {
    setMembers(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  // 勾选/取消单个成员
  const toggleSelect = (index, checked) => {
    setSelectedIdxs(prev => checked ? [...prev, index] : prev.filter(i => i !== index));
  };

  // 全选 / 全不选
  const allSelected = members.length > 0 && selectedIdxs.length === members.length;
  const toggleSelectAll = (checked) => {
    setSelectedIdxs(checked ? members.map((_, i) => i) : []);
  };

  // 批量应用：mode = 'append'（并集追加）| 'replace'（覆盖）
  const applyBatchBosses = (mode) => {
    if (selectedIdxs.length === 0) {
      message.warning('请先勾选要设置的成员');
      return;
    }
    const sel = new Set(selectedIdxs);
    setMembers(prev => prev.map((m, i) => {
      if (!sel.has(i)) return m;
      if (mode === 'replace') return { ...m, participatedBosses: [...batchBosses] };
      // append：与现有并集去重
      const merged = Array.from(new Set([...(m.participatedBosses || []), ...batchBosses]));
      return { ...m, participatedBosses: merged };
    }));
    message.success(`已${mode === 'replace' ? '覆盖' : '追加'} ${selectedIdxs.length} 名成员的参加 Boss`);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (members.length === 0) {
        message.warning('请至少添加一个成员');
        return;
      }
      const emptyNames = members.filter(m => !m.name.trim());
      if (emptyNames.length > 0) {
        message.warning('请填写所有成员的角色名');
        return;
      }
      setLoading(true);
      await onSave({ name: values.name, members });
      setLoading(false);
    } catch (e) {
      if (e.errorFields) return;
      setLoading(false);
    }
  };

  const userCharacterOptions = useMemo(() => {
    if (!currentUser?.characters) return [];
    return currentUser.characters.map(char => ({
      value: char,
      label: (
        <Space>
          <img
            src={`${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(char)}`}
            alt=""
            style={{ width: 24, height: 24, borderRadius: 4 }}
            onError={e => e.target.style.display = 'none'}
          />
          {char}
          <Tag color="green" style={{ fontSize: 10 }}>已绑定</Tag>
        </Space>
      )
    }));
  }, [currentUser?.characters]);

  return (
    <Modal
      title={editingPreset ? '编辑预设小队' : '创建预设小队'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="保存"
      cancelText="取消"
      width={560}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="小队名称" rules={[{ required: true, message: '请输入小队名称' }]}>
          <Input placeholder="例如：周末固定队、主力队" maxLength={30} />
        </Form.Item>
      </Form>

      <Divider>成员列表</Divider>

      {/* 批量设置参加 Boss */}
      {members.length > 0 && (
        <div style={{ background: 'var(--surface-subtle)', border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <Checkbox
              checked={allSelected}
              indeterminate={selectedIdxs.length > 0 && !allSelected}
              onChange={e => toggleSelectAll(e.target.checked)}
            >
              全选
            </Checkbox>
            <Text type="secondary" style={{ fontSize: 12 }}>已选 {selectedIdxs.length} 人，为其批量设置参加 Boss：</Text>
          </div>
          <Select
            mode="multiple"
            allowClear
            size="small"
            placeholder="选择要批量应用的 Boss"
            value={batchBosses}
            onChange={setBatchBosses}
            style={{ width: '100%', marginBottom: 8 }}
            maxTagCount="responsive"
            options={BOSS_RUSH_BOSSES.map(b => ({ value: b.value, label: b.label }))}
          />
          <Space wrap>
            <Button size="small" type="primary" disabled={selectedIdxs.length === 0 || batchBosses.length === 0} onClick={() => applyBatchBosses('append')}>
              追加到选中
            </Button>
            <Button size="small" disabled={selectedIdxs.length === 0} onClick={() => applyBatchBosses('replace')}>
              覆盖选中
            </Button>
            <Text type="secondary" style={{ fontSize: 11 }}>「覆盖」会用上方所选替换（留空即清空）</Text>
          </Space>
        </div>
      )}

      {members.length === 0 ? (
        <Empty description="还没有添加成员" style={{ marginBottom: 16 }} />
      ) : (
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {members.map((member, index) => (
            <div key={index} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Checkbox checked={selectedIdxs.includes(index)} onChange={e => toggleSelect(index, e.target.checked)} />
                <Tag style={{ minWidth: 32, textAlign: 'center', margin: 0 }}>{index + 1}</Tag>
                <AutoComplete
                  value={member.name}
                  onChange={(v) => handleMemberChange(index, 'name', v)}
                  options={userCharacterOptions}
                  filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                  style={{ flex: 1 }}
                >
                  <Input prefix={<UserOutlined />} placeholder="输入角色名" maxLength={12} />
                </AutoComplete>
                <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveMember(index)} />
              </div>
              <Select
                mode="multiple"
                allowClear
                size="small"
                placeholder="参加哪些 Boss（可留空，导入后再设）"
                value={member.participatedBosses || []}
                onChange={(v) => handleMemberChange(index, 'participatedBosses', v)}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                options={BOSS_RUSH_BOSSES.map(b => ({ value: b.value, label: b.label }))}
              />
            </div>
          ))}
        </div>
      )}

      <Button type="dashed" block icon={<PlusOutlined />} onClick={handleAddMember} style={{ marginTop: members.length > 0 ? 8 : 0 }}>
        添加成员
      </Button>
    </Modal>
  );
};

// 库存分组表格 — 同名物品合并展示，展开查看单品详情和操作
const InventoryGroupTable = ({ groups, canManageInventory, updateInventoryMeta, adjustInventoryById, setInventoryItem, deleteInventoryItem, onSell, onBatchSell }) => {
  const dataSource = groups.map((g) => ({
    key: g.name,
    name: g.name,
    totalQty: g.totalQty,
    items: g.items,
  }));

  const ExpandedContent = ({ items, itemName }) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const selectedItems = items.filter(it => selectedRowKeys.includes(it.id));
    const selectedQty = selectedItems.reduce((s, it) => s + (it.quantity || 0), 0);

    return (
      <div>
        {canManageInventory && (
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              size="small"
              type="primary"
              icon={<DollarOutlined />}
              disabled={selectedItems.length === 0}
              onClick={() => onBatchSell({ items: selectedItems, name: itemName, totalQty: selectedQty })}
            >
              批量售出 {selectedItems.length > 0 ? `(${selectedItems.length} 条 / ${selectedQty} 件)` : ''}
            </Button>
            {selectedItems.length > 0 && (
              <Button size="small" onClick={() => setSelectedRowKeys([])}>取消选择</Button>
            )}
          </div>
        )}
        <Table
          dataSource={items}
          rowKey="id"
          size="small"
          pagination={items.length > 20 ? { pageSize: 20, size: 'small', showSizeChanger: false } : false}
          rowSelection={canManageInventory ? {
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          } : undefined}
          columns={[
            {
              title: '来源', width: 180,
              render: (_, r) => {
                const src = r.source || {};
                return (
                  <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 12 }}>{src.runName || '-'}</Text>
                    {src.boss && <Tag style={{ fontSize: 10 }}>{src.boss}</Tag>}
                    <Text type="secondary" style={{ fontSize: 11 }}>{src.addedAt ? new Date(src.addedAt).toLocaleString() : ''}</Text>
                  </Space>
                );
              }
            },
            {
              title: '参与角色', width: 180,
              render: (_, r) => {
                const src = r.source || {};
                return (src.participants || []).length === 0
                  ? <Text type="secondary">-</Text>
                  : <Space wrap size={[2, 2]}>{(src.participants || []).map(p => <Tag key={p} icon={<UserOutlined />} style={{ fontSize: 10 }}>{p}</Tag>)}</Space>;
              }
            },
            { title: 'Leader', dataIndex: 'leader', width: 80, render: l => <Tag color="cyan" style={{ fontSize: 10 }}>{l || '-'}</Tag> },
            {
              title: '数量', dataIndex: 'quantity', width: 50, align: 'center',
              render: (q) => <Text strong style={{ color: '#1677ff' }}>{q}</Text>
            },
            {
              title: '价格 & 渠道', width: 420,
              render: (_, r) => canManageInventory ? (
                <InventoryRowEditor record={r} onSave={updateInventoryMeta} />
              ) : (
                <Space>
                  <Text style={{ color: '#52c41a' }}>{Number(r.mesoValue || 0).toLocaleString()} M</Text>
                  <Text style={{ color: '#faad14' }}>{r.coinValue || 0} C</Text>
                  {r.sellMethod
                    ? <Tag color={r.sellMethod === 'NPC' ? 'blue' : r.sellMethod === 'FM' ? 'purple' : 'default'}>{r.sellMethod}</Tag>
                    : <Text type="secondary">渠道未设置</Text>}
                </Space>
              )
            },
            ...(canManageInventory ? [{
              title: '操作', width: 280,
              render: (_, r) => (
                <Space size="small">
                  {r.quantity === 1 ? (
                    <Popconfirm title={`库存只剩 1 个，确认移除 ${r.name}？`} onConfirm={() => adjustInventoryById(r.id, r.quantity, -1)} okText="确定" cancelText="取消" okButtonProps={{ danger: true }}>
                      <Button size="small" icon={<MinusOutlined />} danger />
                    </Popconfirm>
                  ) : (
                    <Button size="small" icon={<MinusOutlined />} onClick={() => adjustInventoryById(r.id, r.quantity, -1)} disabled={r.quantity <= 0} />
                  )}
                  <Button size="small" icon={<PlusOutlined />} onClick={() => adjustInventoryById(r.id, r.quantity, 1)} />
                  <InputNumber size="small" min={0} style={{ width: 55 }} value={r.quantity}
                    onPressEnter={(e) => setInventoryItem(r.id, parseInt(e.target.value) || 0)}
                    onBlur={(e) => { const v = parseInt(e.target.value) || 0; if (v !== r.quantity) setInventoryItem(r.id, v); }}
                  />
                  <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => onSell(r)} disabled={r.quantity <= 0}>售出</Button>
                  <Popconfirm title={`删除该批次 ${r.name}？`} onConfirm={() => deleteInventoryItem(r.id)} okText="确定" cancelText="取消">
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              )
            }] : [])
          ]}
        />
      </div>
    );
  };

  return (
    <Table
      dataSource={dataSource}
      rowKey="key"
      size="small"
      pagination={false}
      expandable={{
        expandedRowRender: (record) => <ExpandedContent items={record.items} itemName={record.name} />,
        rowExpandable: (r) => r.items.length > 0,
      }}
      columns={[
        {
          title: '物品', dataIndex: 'name', width: 300,
          render: (name) => <Text strong>{name}</Text>
        },
        {
          title: '数量', dataIndex: 'totalQty', width: 80, align: 'center',
          render: (q) => <Text strong style={{ color: '#1677ff', fontSize: 14 }}>{q}</Text>
        }
      ]}
    />
  );
};

// Boss Rush 组件
const BossRush = ({ currentUser }) => {
  const [runs, setRuns] = useState([]);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [characterModalVisible, setCharacterModalVisible] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const [historyViewName, setHistoryViewName] = useState('');
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [editRunNameVisible, setEditRunNameVisible] = useState(false);

  // Ref 跟踪当前 Run ID，避免 loadFromServer 使用过时的闭包值
  const currentRunIdRef = useRef(currentRunId);
  useEffect(() => { currentRunIdRef.current = currentRunId; }, [currentRunId]);

  // 自动保存防抖：定时器 + 待保存的最新角色数据
  const saveTimerRef = useRef(null);
  const pendingCharsRef = useRef(null);
  // saveToServer 的 ref，供在其定义之前的回调（如 switchRun）安全调用
  const saveToServerRef = useRef(null);
  
  // 掉落物品状态
  const [lootConfig, setLootConfig] = useState({ items: [] });
  const [lootForm] = Form.useForm();
  const [lootItems, setLootItems] = useState([]);
  
  // 收益分配状态（保留用于历史记录视图回显，不再用于当前 Run UI）
  const [distributions, setDistributions] = useState([]);
  
  // 全局库存状态（不随 Run 变化）
  const [inventory, setInventory] = useState({ items: [], lastUpdated: null, allLeaders: [] });
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryAddVisible, setInventoryAddVisible] = useState(false);

  // Leader 筛选（管理员可切；非管理员锁定为自己）
  // null = 默认（自己 / admin 看全部）；其他 = 指定 leader 名；'__all__' = 全部
  const [selectedLeader, setSelectedLeader] = useState(null);
  
  // 售出记录状态
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [sellModalVisible, setSellModalVisible] = useState(false);
  const [sellingItem, setSellingItem] = useState(null);  // 当前正在售出的库存项
  
  // 管理员状态
  const [isAdmin, setIsAdmin] = useState(false);

  // 预设小队状态
  const [presetManageVisible, setPresetManageVisible] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(false);

  // 是否可以管理库存：管理员总是可以；非管理员管理自己的库存（selectedLeader 永远为 null）
  const canManageInventory = isAdmin || !selectedLeader;

  const maxRuns = 5;

  // 当前选中的 Run
  const currentRun = useMemo(() => {
    return runs.find(r => r.id === currentRunId) || null;
  }, [runs, currentRunId]);

  // 判断当前用户是否是队长（第一个添加的角色）
  const isCurrentUserHost = useMemo(() => {
    if (!currentUser || !characters.length) return false;
    const leaderCharacter = characters.find(c => c.isLeader);
    if (!leaderCharacter) return false;
    const userChars = currentUser.characters || [];
    return userChars.some(uc => uc.toLowerCase() === leaderCharacter.name.toLowerCase());
  }, [currentUser, characters]);

  // 加载 Runs
  const loadFromServer = useCallback(async () => {
    setLoading(true);
    setIsLoadingData(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/runs`);
      if (res.ok) {
        const data = await res.json();
        // 只加载 NT 类型的 Run
        const ntRuns = (data.runs || []).filter(r => r.bossType === 'NT');
        setRuns(ntRuns);
        setDistributions([]);

        if (ntRuns.length > 0) {
          // 优先保持当前选中的 Run，否则选第一个
          const keepRun = currentRunIdRef.current ? ntRuns.find(r => r.id === currentRunIdRef.current) : null;
          const targetRun = keepRun || ntRuns[0];
          setCurrentRunId(targetRun.id);
          setCharacters(targetRun.characters || []);
          // loot 数据由监听 currentRunKey 的 useEffect 统一加载，这里不再重复请求
        } else {
          setCurrentRunId(null);
          setCharacters([]);
          setLootItems([]);
        }
        setIsHistoryView(false);
        setHistoryViewName('');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
    setTimeout(() => setIsLoadingData(false), 100);
  }, []);

  // 加载历史记录（只加载 Boss Rush/NT 类型）
  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/history?boss_type=NT`);
      if (res.ok) {
        const data = await res.json();
        setHistoryList(data);
      }
    } catch (e) { console.error(e); }
  }, []);

  // 加载掉落物品配置
  const loadLootConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loot-config`);
      if (res.ok) {
        const data = await res.json();
        setLootConfig(data);
      }
    } catch (e) { console.error(e); }
  }, []);

  // 加载全局库存（按 leader 过滤）
  const loadInventory = useCallback(async () => {
    setInventoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentUser?.username) params.set('username', currentUser.username);
      if (selectedLeader) {
        params.set('leader', selectedLeader);
      } else if (currentUser?.username) {
        // null = "仅自己"，显式传自己用户名作为 leader 过滤
        params.set('leader', currentUser.username);
      }
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/inventory?${params}`);
      if (res.ok) {
        const data = await res.json();
        setInventory(data || { items: [], lastUpdated: null, allLeaders: [] });
      }
    } catch (e) { console.error(e); }
    setInventoryLoading(false);
  }, [currentUser?.username, selectedLeader]);

  // 手动添加库存项（每次新增一条独立记录，不与同名合并）
  const addInventoryItem = useCallback(async (name, quantity, participants = null) => {
    if (!currentUser?.username) return;
    try {
      const body = { name, quantity };
      if (participants && participants.length > 0) body.participants = participants;
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/inventory?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        await loadInventory();
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '添加失败'));
      }
    } catch (e) {
      messageApi.error('添加失败');
    }
  }, [currentUser?.username, messageApi, loadInventory]);

  // 设置库存项数量（绝对值，按 ID）
  const setInventoryItem = useCallback(async (itemId, quantity) => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/inventory/item?item_id=${encodeURIComponent(itemId)}&username=${encodeURIComponent(currentUser.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity })
      });
      if (res.ok) {
        await loadInventory();
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '操作失败'));
      }
    } catch (e) {
      messageApi.error('操作失败');
    }
  }, [currentUser?.username, messageApi, loadInventory]);

  // 调整库存项数量（基于 ID + 当前数量 + delta）
  const adjustInventoryById = useCallback(async (itemId, currentQty, delta) => {
    const newQty = Math.max(0, (currentQty || 0) + delta);
    await setInventoryItem(itemId, newQty);
  }, [setInventoryItem]);

  // 删除库存项（按 ID）
  const deleteInventoryItem = useCallback(async (itemId) => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/inventory/item?item_id=${encodeURIComponent(itemId)}&username=${encodeURIComponent(currentUser.username)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await loadInventory();
        messageApi.success('已删除');
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '删除失败'));
      }
    } catch (e) {
      messageApi.error('删除失败');
    }
  }, [currentUser?.username, messageApi, loadInventory]);

  // 更新库存项元数据（定价/销售渠道，按 ID）
  const updateInventoryMeta = useCallback(async (itemId, meta) => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/inventory/item/meta?item_id=${encodeURIComponent(itemId)}&username=${encodeURIComponent(currentUser.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(meta)
      });
      if (res.ok) {
        await loadInventory();
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '更新失败'));
      }
    } catch (e) {
      messageApi.error('更新失败');
    }
  }, [currentUser?.username, messageApi, loadInventory]);

  // 加载售出记录（按 leader 过滤）
  const loadSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentUser?.username) params.set('username', currentUser.username);
      if (selectedLeader) {
        params.set('leader', selectedLeader);
      } else if (currentUser?.username) {
        params.set('leader', currentUser.username);
      }
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/sales?${params}`);
      if (res.ok) {
        const data = await res.json();
        setSales(data.sales || []);
      }
    } catch (e) { console.error(e); }
    setSalesLoading(false);
  }, [currentUser?.username, selectedLeader]);

  // 售出库存物品
  const sellInventory = useCallback(async (payload) => {
    if (!currentUser?.username) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/sales?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        await loadInventory();
        await loadSales();
        messageApi.success(`已售出 ${payload.quantity} × ${payload.itemName}`);
        return true;
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '售出失败'));
        return false;
      }
    } catch (e) {
      messageApi.error('售出失败');
      return false;
    }
  }, [currentUser?.username, messageApi, loadSales, loadInventory]);

  // 撤销售出（退回库存）
  const revokeSale = useCallback(async (saleId) => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/sales/${saleId}?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await loadInventory();
        await loadSales();
        messageApi.success('已撤销售出，物品退回库存');
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '撤销失败'));
      }
    } catch (e) {
      messageApi.error('撤销失败');
    }
  }, [currentUser?.username, messageApi, loadSales, loadInventory]);

  // 标记已分配（移除该售出记录，不影响库存）
  const distributeSale = useCallback(async (saleId) => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/sales/${saleId}/distribute?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await loadSales();
        messageApi.success('已标记为已分配，记录已归档');
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '操作失败'));
      }
    } catch (e) {
      messageApi.error('操作失败');
    }
  }, [currentUser?.username, messageApi, loadSales]);

  // 按受益人标记已分配（跨所有售出记录）
  const distributeBeneficiary = useCallback(async (beneficiaryName) => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/sales/distribute-beneficiary?username=${encodeURIComponent(currentUser.username)}&beneficiary=${encodeURIComponent(beneficiaryName)}`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        await loadSales();
        messageApi.success(`已标记 ${beneficiaryName} 分配完成（涉及 ${data.affected} 笔，移除 ${data.removed} 笔）`);
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '操作失败'));
      }
    } catch (e) {
      messageApi.error('操作失败');
    }
  }, [currentUser?.username, messageApi, loadSales]);

  // 静态数据（runs / history / loot-config）只在挂载时加载一次，不随 selectedLeader 变化重跑
  useEffect(() => { loadFromServer(); loadHistory(); loadLootConfig(); }, [loadFromServer, loadHistory, loadLootConfig]);

  // 库存 / 售出 数据依赖 selectedLeader，单独加载：切换 leader 时只重拉这两项，而非全部
  useEffect(() => { loadInventory(); loadSales(); }, [loadInventory, loadSales]);

  // 检查管理员状态
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(currentUser.username)}/check-admin`);
        if (res.ok) {
          const data = await res.json();
          setIsAdmin(data.isAdmin || false);
        }
      } catch (e) {
        console.error(e);
      }
    };
    checkAdminStatus();
  }, [currentUser]);

  // 切换 Run
  const switchRun = useCallback((runId) => {
    // 切换前先把当前 Run 未保存（防抖等待中）的改动立即落库
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (pendingCharsRef.current && saveToServerRef.current) {
      saveToServerRef.current(pendingCharsRef.current);
      pendingCharsRef.current = null;
    }
    const run = runs.find(r => r.id === runId);
    if (run) {
      setCurrentRunId(runId);
      setCharacters(run.characters || []);
      setLootItems([]);        // 切换 Run 时立刻清空旧 loot，避免视觉残留
      setDistributions([]);    // 同清旧分配数据
      setIsHistoryView(false);
    }
  }, [runs]);

  // 保存到服务器
  const saveToServer = useCallback(async (chars) => {
    if (!currentRunId || !currentUser) return;
    setSyncing(true);
    try {
      await fetch(`${API_BASE_URL}/api/runs/${currentRunId}?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characters: chars, team_count: 1 })
      });
      setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, characters: chars } : r));
    } catch (e) { console.error(e); }
    setSyncing(false);
  }, [currentRunId, currentUser]);

  // 保持 saveToServerRef 指向最新的 saveToServer，供 switchRun 等前置回调调用
  useEffect(() => { saveToServerRef.current = saveToServer; }, [saveToServer]);

  // 自动保存（防抖）：连续勾选/编辑角色时合并为一次 PUT，停止操作 800ms 后落库。
  // 切换 Run、卸载、完成/删除等场景通过 flushSave 立即保存，避免丢失未提交的改动。
  const flushSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (pendingCharsRef.current) {
      const chars = pendingCharsRef.current;
      pendingCharsRef.current = null;
      saveToServer(chars);
    }
  }, [saveToServer]);

  useEffect(() => {
    if (!isLoadingData && !isHistoryView && currentRunId && characters.length > 0) {
      pendingCharsRef.current = characters;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        const chars = pendingCharsRef.current;
        pendingCharsRef.current = null;
        if (chars) saveToServer(chars);
      }, 800);
    }
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [characters, saveToServer, isHistoryView, isLoadingData, currentRunId]);

  // 组件卸载时把未保存的改动强制落库
  useEffect(() => () => { flushSave(); }, [flushSave]);

  // 创建新 Run
  const handleCreateRun = async (values) => {
    try {
      const runName = (values.name || '').trim();
      const res = await fetch(`${API_BASE_URL}/api/runs?name=${encodeURIComponent(runName)}&boss_type=NT&created_by=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'POST'
      });

      if (!res.ok) {
        const data = await res.json();
        messageApi.error(getErrorText(data, '创建失败'));
        return;
      }

      const runData = await res.json();
      const newRun = runData.run;

      // 构建初始角色列表：Host + 预设小队成员
      let initChars = [];

      // 若选了预设小队，先加载，便于 Host 继承同名成员的参加 Boss
      let presetMembers = [];
      if (values.presetId) {
        try {
          const presetsRes = await fetch(`${API_BASE_URL}/api/preset-teams?username=${encodeURIComponent(currentUser?.username || '')}`);
          if (presetsRes.ok) {
            const presetsData = await presetsRes.json();
            const preset = (presetsData.presets || []).find(p => p.id === values.presetId);
            if (preset) presetMembers = preset.members || [];
          }
        } catch (e) { console.error('加载预设小队失败:', e); }
      }

      const hostName = values.hostCharacter.trim();
      // Host 若也在预设中，继承其参加的 Boss，避免数据丢失
      const hostPresetMember = presetMembers.find(m => (m.name || '').trim().toLowerCase() === hostName.toLowerCase());

      // Host 角色
      const hostCharacter = {
        id: Date.now(),
        name: hostName,
        team: 1,
        isLeader: true,
        participatedBosses: hostPresetMember?.participatedBosses || [],
        addedAt: new Date().toISOString()
      };
      initChars.push(hostCharacter);
      const existingNames = new Set([hostCharacter.name.toLowerCase()]);

      // 加入预设小队其余成员（跳过与 Host 重名的，Host 已单独处理）
      let nextId = hostCharacter.id + 1;
      presetMembers.forEach(m => {
        const name = (m.name || '').trim();
        if (name && !existingNames.has(name.toLowerCase())) {
          initChars.push({
            id: nextId++,
            name,
            team: 1,
            isLeader: false,
            participatedBosses: m.participatedBosses || [],
            addedAt: new Date().toISOString()
          });
          existingNames.add(name.toLowerCase());
        }
      });

      const updateRes = await fetch(`${API_BASE_URL}/api/runs/${newRun.id}?username=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characters: initChars, team_count: 1 })
      });

      if (updateRes.ok) {
        newRun.characters = initChars;
        newRun.teamCount = 1;
        setRuns(prev => [...prev, newRun]);
        setCurrentRunId(newRun.id);
        setCharacters(initChars);
        setLootItems([]);
        setDistributions([]);
        setIsHistoryView(false);
        setCreateModalVisible(false);

        const presetCount = initChars.length - 1;
        messageApi.success(
          `Boss Rush "${runName}" 创建成功` +
          (presetCount > 0 ? `，已导入预设小队 ${presetCount} 人` : '')
        );
      } else {
        await fetch(`${API_BASE_URL}/api/runs/${newRun.id}?username=${encodeURIComponent(currentUser?.username || '')}`, { method: 'DELETE' });
        messageApi.error('创建失败');
      }
    } catch (e) {
      messageApi.error('创建失败: ' + e.message);
    }
  };

  // 删除 Run
  const handleDeleteRun = async () => {
    if (!currentRunId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRunId}?username=${encodeURIComponent(currentUser.username)}`, { method: 'DELETE' });
      if (res.ok) {
        setRuns(prev => prev.filter(r => r.id !== currentRunId));
        const remaining = runs.filter(r => r.id !== currentRunId);
        if (remaining.length > 0) {
          setCurrentRunId(remaining[0].id);
          setCharacters(remaining[0].characters || []);
        } else {
          setCurrentRunId(null);
          setCharacters([]);
        }
        // 清空掉落物品和分配数据
        setLootItems([]);
        setDistributions([]);
        messageApi.success('Run 已删除');
      }
    } catch (e) {
      messageApi.error('删除失败');
    }
  };

  // 应用预设小队到当前 Run
  const handleApplyPreset = useCallback(async (preset) => {
    if (!currentRunId || !currentUser) return;
    setApplyingPreset(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/runs/${currentRunId}/apply-preset?preset_id=${preset.id}&username=${encodeURIComponent(currentUser.username)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (res.ok) {
        messageApi.success(data.message || '预设小队已应用');
        // 只刷新当前 Run，避免 loadFromServer 跳到第一个 Run
        const runRes = await fetch(`${API_BASE_URL}/api/runs/${currentRunId}`);
        if (runRes.ok) {
          const run = await runRes.json();
          setCharacters(run.characters || []);
          setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, ...run } : r));
        }
      } else {
        messageApi.error(getErrorText(data, '应用失败'));
      }
    } catch (e) {
      console.error(e);
      messageApi.error('应用失败');
    }
    setApplyingPreset(false);
  }, [currentRunId, currentUser, messageApi]);

  // 添加角色
  const handleSaveCharacter = (values) => {
    const newChar = {
      id: Date.now(),
      ...values,
      team: 1,
      isLeader: characters.length === 0,  // 第一个角色自动为队长
      participatedBosses: [],
      addedAt: new Date().toISOString()
    };
    setCharacters(prev => [...prev, newChar]);
    messageApi.success('角色已添加');
    setCharacterModalVisible(false);
  };

  // 删除角色
  const handleDeleteCharacter = (charId) => {
    const char = characters.find(c => c.id === charId);
    if (char?.roles?.includes('host') && characters.length > 1) {
      messageApi.error('Host 不能直接删除，请先转移 Host');
      return;
    }
    setCharacters(prev => prev.filter(c => c.id !== charId));
    messageApi.success('角色已删除');
  };

  // 更新状态
  // 更新角色参与的 Boss
  const handleBossesChange = (charId, bosses) => {
    setCharacters(prev => prev.map(c => c.id === charId ? { ...c, participatedBosses: bosses } : c));
  };

  // 批量更新参与 Boss
  const handleBatchUpdate = (bossesMap) => {
    setCharacters(prev => prev.map(c => bossesMap[c.id] ? { ...c, participatedBosses: bossesMap[c.id] } : c));
    messageApi.success('参与 Boss 已更新');
    setBatchModalVisible(false);
  };

  // 完成 Run
  const handleCompleteRun = async (runName) => {
    if (!runName.trim()) { messageApi.warning('请输入运行名称'); return; }
    try {
      // 直接根据当前 lootItems 计算总额（不再依赖 distributions）
      const mesoTotal = lootSummary.meso;
      const coinTotal = lootSummary.coin;
      
      const res = await fetch(`${API_BASE_URL}/api/history/complete?username=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          run_name: runName.trim(),
          run_id: currentRunId,
          lootItems: lootItems,
          distributions: [],  // Boss Rush 不再做分配，留空
          mesoTotal,
          coinTotal
        })
      });
      if (res.ok) {
        messageApi.success('运行已完成并保存到历史记录');
        loadFromServer();
        loadHistory();
        loadInventory();  // Run 完成后刷新库存（已自动入库）
        setCompleteModalVisible(false);
        // 清空掉落物品和分配数据
        setLootItems([]);
        setDistributions([]);
      } else {
        const data = await res.json();
        messageApi.error(getErrorText(data, '完成失败'));
      }
    } catch (e) {
      messageApi.error('完成失败');
    }
  };

  // 加载历史记录
  const handleLoadHistory = async (runId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${runId}`);
      if (res.ok) {
        const data = await res.json();
        setCharacters(data.characters || []);
        setIsHistoryView(true);
        setHistoryViewName(data.runName);
        // 加载掉落物品和分配数据
        setLootItems(data.lootItems || []);
        setDistributions(data.distributions || []);
        // 设置一个虚拟的 currentRunId 来触发渲染（使用历史记录的 runId）
        setCurrentRunId(runId);
        // 添加一个虚拟 run 到 runs 数组以满足 currentRun 的依赖
        setRuns(prev => {
          const existsVirtual = prev.find(r => r.id === runId);
          if (existsVirtual) return prev;
          return [...prev, { id: runId, name: data.runName, bossType: data.bossType || 'NT', isVirtual: true }];
        });
        messageApi.success('已加载历史记录');
      }
    } catch (e) {
      messageApi.error('加载失败');
    }
  };

  // 添加掉落物品
  const handleAddLoot = async (itemName, quantity = 1, boss = null) => {
    // 检查物品是否存在于掉落配置中
    const itemConfig = lootConfig.items?.find(i => i.name.toLowerCase() === itemName.toLowerCase());

    // 如果物品不存在，尝试登记到全局掉落配置（用于统一定价）。
    // 登记全局配置需要管理员权限（后端 /api/loot-config 为 admin-only），非管理员没有权限，
    // 但仍应允许其记录本次掉落 —— 只是该物品不会出现在全局价格配置中，不阻塞本次操作。
    if (!itemConfig) {
      if (isAdmin) {
        const saved = await saveNewItemToConfig(itemName);
        if (saved) {
          try {
            const res = await fetch(`${API_BASE_URL}/api/loot-config`);
            if (res.ok) setLootConfig(await res.json());
          } catch (e) { console.error(e); }
        }
      } else {
        messageApi.info(`"${itemName}" 尚未登记价格，已记录本次掉落，价格可在售出时手动设置`);
      }
    }

    setLootItems(prev => {
      const key = boss ? `${itemName}_${boss}` : itemName;
      const existing = prev.find(l => (l.boss ? `${l.name}_${l.boss}` : l.name) === key);
      if (existing) {
        return prev.map(l => ((l.boss ? `${l.name}_${l.boss}` : l.name) === key)
          ? { ...l, quantity: l.quantity + quantity }
          : l
        );
      }
      return [...prev, {
        name: itemName,
        quantity,
        boss,
        sellMethod: 'NPC',
        customMeso: null,
        customCoin: null
      }];
    });
  };

  // 删除掉落物品
  const handleDeleteLoot = (itemKey) => {
    setLootItems(prev => prev.filter(l => {
      const key = l.boss ? `${l.name}_${l.boss}` : l.name;
      return key !== itemKey;
    }));
  };


  // 更新物品销售方式
  const handleUpdateSellMethod = (itemKey, sellMethod, customMeso, customCoin, currencyType) => {
    setLootItems(prev => prev.map(item => {
      const key = item.boss ? `${item.name}_${item.boss}` : item.name;
      if (key !== itemKey) return item;

      if (sellMethod === 'NPC') {
        const cfg = lootConfig.items?.find(i => i.name === item.name);
        if (cfg && (cfg.mesoValue > 0 || cfg.coinValue > 0)) {
          return { ...item, sellMethod: 'NPC', customMeso: null, customCoin: null, currencyType: null };
        }
      }

      return {
        ...item,
        sellMethod,
        customMeso: sellMethod === 'NPC' ? (item.customMeso ?? 0) : (customMeso !== undefined ? customMeso : (item.customMeso ?? 0)),
        customCoin: sellMethod === 'NPC' ? (item.customCoin ?? 0) : (customCoin !== undefined ? customCoin : (item.customCoin ?? 0)),
        currencyType: sellMethod === 'NPC' ? null : (currencyType || item.currencyType || 'meso')
      };
    }));
  };

  // 更新某个物品的 NPC 价格（直接在 loot 表中编辑）
  const handleUpdateNpcPrice = (itemKey, mesoVal, coinVal) => {
    setLootItems(prev => prev.map(item => {
      const key = item.boss ? `${item.name}_${item.boss}` : item.name;
      if (key !== itemKey) return item;
      return { ...item, customMeso: mesoVal, customCoin: coinVal };
    }));
  };

  // 同步单个物品价格到 loot_config
  const syncItemPriceToConfig = async (itemName, mesoValue, coinValue) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loot-config`);
      if (!res.ok) return;
      const config = await res.json();
      const items = config.items || [];
      const idx = items.findIndex(i => i.name.toLowerCase() === itemName.toLowerCase());
      if (idx >= 0) {
        items[idx] = { ...items[idx], mesoValue, coinValue };
      } else {
        items.push({ name: itemName, mesoValue, coinValue, bossType: 'NT' });
      }
      await fetch(`${API_BASE_URL}/api/loot-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, items })
      });
      // 刷新本地 lootConfig
      setLootConfig(prev => {
        const list = [...(prev.items || [])];
        const i = list.findIndex(it => it.name.toLowerCase() === itemName.toLowerCase());
        if (i >= 0) list[i] = { ...list[i], mesoValue, coinValue };
        else list.push({ name: itemName, mesoValue, coinValue, bossType: 'NT' });
        return { ...prev, items: list };
      });
    } catch (e) { console.error('同步价格失败:', e); }
  };

  // 获取当前 Run 的唯一标识
  const currentRunKey = useMemo(() => {
    if (currentRun) return `run_${currentRun.id}`;
    return null;
  }, [currentRun]);

  // 用 ref 跟踪当前激活的 runKey，避免异步 fetch 竞态时旧响应覆盖新数据
  const activeRunKeyRef = useRef(null);
  useEffect(() => { activeRunKeyRef.current = currentRunKey; }, [currentRunKey]);

  // 加载当前 Run 的掉落物品数据
  const loadLootData = useCallback(async () => {
    if (!currentRunKey) return;
    const requestKey = currentRunKey;
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/loot/${currentRunKey}`);
      if (res.ok) {
        const data = await res.json();
        // 仅当请求时的 key 仍为当前激活 Run 才更新（防快速切换竞态）
        if (requestKey === activeRunKeyRef.current) {
          setLootItems(data.lootItems || []);
        }
      }
    } catch (e) { console.error(e); }
  }, [currentRunKey]);

  // 当 Run 切换时加载掉落物品
  useEffect(() => {
    if (currentRunKey && !isHistoryView) {
      loadLootData();
    }
  }, [currentRunKey, isHistoryView, loadLootData]);

  // 保存掉落物品数据（独立存储到 Boss Rush 文件夹）
  const saveLootData = async () => {
    if (!currentRunKey) {
      messageApi.warning('请先选择一个 Run');
      return;
    }

    // 保存前：把 NPC 物品的自定义价格同步到 loot_config
    const npcItems = lootItems.filter(l => l.sellMethod === 'NPC' && l.customMeso > 0);
    for (const item of npcItems) {
      const cfg = lootConfig.items?.find(i => i.name === item.name);
      if (!cfg || cfg.mesoValue !== (item.customMeso || 0)) {
        await syncItemPriceToConfig(item.name, item.customMeso || 0, 0);
      }
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-rush/loot/${currentRunKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId: currentRunKey,
          lootItems: lootItems
        })
      });
      if (res.ok) {
        const data = await res.json();
        messageApi.success(`掉落物品已保存，总计: ${data.totalMeso?.toLocaleString() || 0} Meso / ${data.totalCoin || 0} Coin`);
      } else {
        messageApi.error('保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败');
    }
  };

  // 计算掉落物品总价值
  const lootSummary = useMemo(() => {
    let meso = 0, coin = 0;
    (lootItems || []).forEach(item => {
      const itemConfig = lootConfig.items?.find(i => i.name === item.name);
      const qty = item.quantity || 1;
      if (item.sellMethod === 'NPC') {
        if (itemConfig && itemConfig.mesoValue > 0) {
          meso += itemConfig.mesoValue * qty;
        } else {
          meso += (item.customMeso || 0) * qty;
        }
      } else {
        meso += (item.customMeso || 0) * qty;
        coin += (item.customCoin || 0) * qty;
      }
    });
    return { meso, coin };
  }, [lootItems, lootConfig.items]);

  // 过滤掉落物品（NT Boss 专用）
  const filteredLootItems = useMemo(() => {
    return (lootConfig.items || []).filter(item =>
      item.bossType === 'NT' || !item.bossType
    );
  }, [lootConfig.items]);

  // 判断是否为 Auf 特殊物品（仅按物品名称判断）
  const isAufItem = useCallback((item) => {
    const name = (item.name || '').toUpperCase();
    return name.includes('AUF') || name.includes('欧碧拉');
  }, []);

  // 分组库存：Auf 物品单独分列，同名物品合并
  const groupedInventory = useMemo(() => {
    const items = inventory.items || [];
    const aufItems = [];
    const otherItems = [];

    items.forEach(item => {
      if (isAufItem(item)) {
        aufItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    const groupByName = (list) => {
      const groups = {};
      list.forEach(item => {
        const name = item.name;
        if (!groups[name]) groups[name] = { name, totalQty: 0, items: [] };
        groups[name].totalQty += (item.quantity || 0);
        groups[name].items.push(item);
      });
      return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
    };

    return { auf: groupByName(aufItems), other: groupByName(otherItems) };
  }, [inventory.items, isAufItem]);
  const knownCharacters = useMemo(() => {
    const set = new Set();
    (characters || []).forEach(c => c.name && set.add(c.name));
    (inventory.items || []).forEach(it => {
      (it.source?.participants || []).forEach(p => p && set.add(p));
    });
    (sales || []).forEach(s => {
      (s.beneficiaries || []).forEach(b => b.name && set.add(b.name));
    });
    return Array.from(set).sort();
  }, [characters, inventory, sales]);

  // 保存新物品到掉落配置
  const saveNewItemToConfig = async (itemName) => {
    try {
      // 获取当前掉落物品配置（loot-config，不是 boss-config）
      const res = await fetch(`${API_BASE_URL}/api/loot-config`);
      if (!res.ok) return false;
      const config = await res.json();

      // 检查物品是否已存在
      const exists = config.items?.some(i => i.name.toLowerCase() === itemName.toLowerCase());
      if (exists) return true;

      // 添加新物品
      const newConfig = {
        ...config,
        items: [...(config.items || []), {
          name: itemName,
          mesoValue: 0,
          coinValue: 0,
          bossType: 'NT'
        }]
      };

      // 保存到 loot-config
      const saveRes = await fetch(`${API_BASE_URL}/api/loot-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newConfig)
      });
      
      if (saveRes.ok) {
        // 更新本地配置
        setLootConfig(newConfig);
        messageApi.success(`新物品 "${itemName}" 已添加到配置`);
        return true;
      }
      return false;
    } catch (e) {
      console.error('保存新物品失败:', e);
      return false;
    }
  };

  // 计算分配（已废弃：库存机制下不再使用，但保留代码备查/便于回滚）
  // eslint-disable-next-line no-unused-vars
  const calculateDistribution = () => {
    if (!currentRun || !characters.length) {
      messageApi.warning('请先添加角色');
      return;
    }
    
    if (lootItems.length === 0) {
      messageApi.warning('请先登记掉落物品');
      return;
    }
    
    // 找出 Host 角色
    const hostChar = characters.find(c => c.isLeader);
    const hostName = hostChar?.name;
    
    // 初始化每个角色的分配结果，保留现有比例和Mule状态
    const charDistMap = {};
    characters.forEach(c => {
      const existingDist = distributions.find(d => d.characterName === c.name);
      charDistMap[c.name] = {
        characterName: c.name,
        participatedBosses: c.participatedBosses || [],
        bossCount: (c.participatedBosses || []).length,
        ratio: existingDist?.ratio ?? 1, // 保留现有比例或默认为1
        isMule: existingDist?.isMule ?? false, // 保留Mule状态
        meso: 0,
        coin: 0,
        itemDetails: []
      };
    });
    
    // 遍历每个掉落物品，计算分配
    lootItems.forEach(item => {
      const itemBoss = item.boss;
      if (!itemBoss) return;
      
      // 找出参与了该 Boss-Round 的角色
      const participatingChars = characters.filter(c => 
        (c.participatedBosses || []).includes(itemBoss)
      );
      
      if (participatingChars.length === 0) return;
      
      // 计算该物品的价值
      const itemConfig = lootConfig.items?.find(i => i.name === item.name);
      const qty = item.quantity || 1;
      let itemMeso = 0;
      let itemCoin = 0;
      
      if (item.sellMethod === 'NPC') {
        if (itemConfig) {
          itemMeso = (itemConfig.mesoValue || 0) * qty;
          itemCoin = (itemConfig.coinValue || 0) * qty;
        }
      } else {
        itemMeso = (item.customMeso || 0) * qty;
        itemCoin = (item.customCoin || 0) * qty;
      }
      
      // 计算参与角色的总比例
      const totalRatio = participatingChars.reduce((s, c) => s + (charDistMap[c.name].ratio || 1), 0);
      
      // Meso 使用最大余额法分配，确保总额精确
      if (itemMeso > 0 && totalRatio > 0) {
        const mesoShares = distributeMesoWithRemainder(itemMeso, participatingChars, charDistMap, totalRatio);
        mesoShares.forEach(({ name, meso }) => {
          charDistMap[name].meso += meso;
        });
      }
      
      // Coin 整数分配（不能有小数，Mule 不参与）
      if (itemCoin > 0) {
        // 过滤掉 Mule 角色
        const nonMuleChars = participatingChars.filter(c => !charDistMap[c.name].isMule);
        if (nonMuleChars.length > 0) {
          const coinDistribution = distributeCoinsIntegerly(itemCoin, nonMuleChars, charDistMap, hostName);
          coinDistribution.forEach(({ name, coin }) => {
            charDistMap[name].coin += coin;
          });
        }
      }
      
      // 记录物品详情
      participatingChars.forEach(c => {
        charDistMap[c.name].itemDetails.push({
          itemName: item.name,
          boss: itemBoss
        });
      });
    });
    
    const newDistributions = Object.values(charDistMap);
    setDistributions(newDistributions);
    messageApi.success('分配计算完成');
  };

  // Meso 最大余额法分配：确保总额精确
  const distributeMesoWithRemainder = (totalMeso, participatingChars, charDistMap, totalRatio) => {
    // 计算每个角色的精确份额
    const shares = participatingChars.map(c => {
      const ratio = charDistMap[c.name].ratio || 1;
      const exactShare = totalMeso * ratio / totalRatio;
      return {
        name: c.name,
        exactShare,
        floorShare: Math.floor(exactShare),  // 向下取整（整数 Meso）
        remainder: exactShare - Math.floor(exactShare)  // 小数部分
      };
    });
    
    // 计算已分配的总额和剩余金额
    const distributed = shares.reduce((s, sh) => s + sh.floorShare, 0);
    let remaining = totalMeso - distributed;
    
    // 按 remainder 降序排序
    const sorted = [...shares].sort((a, b) => b.remainder - a.remainder);
    
    // 将剩余的 Meso 按 1 分配给余数最大的角色
    sorted.forEach(sh => {
      if (remaining > 0) {
        sh.floorShare += 1;
        remaining -= 1;
      }
    });
    
    return shares.map(sh => ({ name: sh.name, meso: sh.floorShare }));
  };

  // Coin 整数分配函数：按比例分配，余数优先给 Host
  const distributeCoinsIntegerly = useCallback((totalCoins, participatingChars, charDistMap, hostName) => {
    const totalRatio = participatingChars.reduce((s, c) => s + (charDistMap[c.name].ratio || 1), 0);
    
    // 计算每个角色的基础份额和小数部分
    const shares = participatingChars.map(c => {
      const ratio = charDistMap[c.name].ratio || 1;
      const exactShare = totalCoins * ratio / totalRatio;
      return {
        name: c.name,
        isHost: c.name === hostName,
        exactShare,
        baseShare: Math.floor(exactShare),
        remainder: exactShare - Math.floor(exactShare)
      };
    });
    
    // 计算已分配的 Coin 和剩余的 Coin
    const distributed = shares.reduce((s, sh) => s + sh.baseShare, 0);
    let remaining = totalCoins - distributed;
    
    // 按 remainder 降序排序，Host 在同等条件下优先
    shares.sort((a, b) => {
      if (a.isHost && !b.isHost) return -1;
      if (!a.isHost && b.isHost) return 1;
      return b.remainder - a.remainder;
    });
    
    // 分配剩余的 Coin
    for (let i = 0; i < shares.length && remaining > 0; i++) {
      shares[i].baseShare += 1;
      remaining--;
    }
    
    return shares.map(sh => ({ name: sh.name, coin: sh.baseShare }));
  }, []);

  // 更新角色的分配比例
  const updateCharRatio = useCallback((charName, newRatio) => {
    setDistributions(prev => prev.map(d => 
      d.characterName === charName ? { ...d, ratio: newRatio } : d
    ));
  }, []);

  // 基于现有分配数据重新计算（用于 Mule 变化后）
  const recalculateWithDistributions = useCallback((currentDistributions) => {
    if (!currentRun || !characters.length || lootItems.length === 0) return;
    
    const hostChar = characters.find(c => c.isLeader);
    const hostName = hostChar?.name;
    
    // 使用传入的分配数据初始化
    const charDistMap = {};
    characters.forEach(c => {
      const existingDist = currentDistributions.find(d => d.characterName === c.name);
      charDistMap[c.name] = {
        characterName: c.name,
        participatedBosses: c.participatedBosses || [],
        bossCount: (c.participatedBosses || []).length,
        ratio: existingDist?.ratio ?? 1,
        isMule: existingDist?.isMule ?? false,
        meso: 0,
        coin: 0,
        itemDetails: []
      };
    });
    
    // 遍历每个掉落物品，计算分配
    lootItems.forEach(item => {
      const itemBoss = item.boss;
      if (!itemBoss) return;
      
      const participatingChars = characters.filter(c => 
        (c.participatedBosses || []).includes(itemBoss)
      );
      
      if (participatingChars.length === 0) return;
      
      const itemConfig = lootConfig.items?.find(i => i.name === item.name);
      const qty = item.quantity || 1;
      let itemMeso = 0;
      let itemCoin = 0;
      
      if (item.sellMethod === 'NPC') {
        if (itemConfig) {
          itemMeso = (itemConfig.mesoValue || 0) * qty;
          itemCoin = (itemConfig.coinValue || 0) * qty;
        }
      } else {
        itemMeso = (item.customMeso || 0) * qty;
        itemCoin = (item.customCoin || 0) * qty;
      }
      
      const totalRatio = participatingChars.reduce((s, c) => s + (charDistMap[c.name].ratio || 1), 0);
      
      participatingChars.forEach(c => {
        const ratio = charDistMap[c.name].ratio || 1;
        const mesoShare = Math.round(itemMeso * ratio / totalRatio);
        charDistMap[c.name].meso += mesoShare;
      });
      
      if (itemCoin > 0) {
        const nonMuleChars = participatingChars.filter(c => !charDistMap[c.name].isMule);
        if (nonMuleChars.length > 0) {
          const coinDistribution = distributeCoinsIntegerly(itemCoin, nonMuleChars, charDistMap, hostName);
          coinDistribution.forEach(({ name, coin }) => {
            charDistMap[name].coin += coin;
          });
        }
      }
      
      participatingChars.forEach(c => {
        charDistMap[c.name].itemDetails.push({
          itemName: item.name,
          boss: itemBoss
        });
      });
    });
    
    setDistributions(Object.values(charDistMap));
  }, [currentRun, characters, lootItems, lootConfig.items, distributeCoinsIntegerly]);

  // 更新角色的 Mule 状态并重新计算分配
  const updateCharMule = useCallback((charName, isMule) => {
    // 先更新 Mule 状态
    setDistributions(prev => {
      const updated = prev.map(d => 
        d.characterName === charName ? { ...d, isMule } : d
      );
      // 异步触发重新计算
      setTimeout(() => recalculateWithDistributions(updated), 0);
      return updated;
    });
  }, [recalculateWithDistributions]);

  // 清空分配（已废弃）
  // eslint-disable-next-line no-unused-vars
  const clearDistribution = () => {
    setDistributions([]);
  };

  // 当角色变化时，清空分配（仅在非历史视图模式）
  useEffect(() => {
    if (!isHistoryView) {
      setDistributions([]);
    }
  }, [characters.length, isHistoryView]);

  // 分配表格列（已废弃）
  // eslint-disable-next-line no-unused-vars
  const distributionColumns = useMemo(() => [
    { 
      title: '角色', 
      dataIndex: 'characterName', 
      width: 150,
      render: (name, record) => {
        const isHost = characters.find(c => c.name === name)?.isLeader;
        return (
          <Space>
            <img 
              src={`${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(name)}`}
              alt=""
              style={{ width: 24, height: 24, borderRadius: 4 }}
              onError={e => e.target.style.display = 'none'}
            />
            {name}
            {isHost && <Tag color="gold" style={{ fontSize: 10 }}>Host</Tag>}
          </Space>
        );
      }
    },
    { 
      title: '参与 Boss', 
      dataIndex: 'participatedBosses',
      render: (bosses) => (
        <Space size={2} wrap>
          {(bosses || []).map(b => {
            const bossConfig = BOSS_RUSH_BOSSES.find(o => o.value === b);
            return bossConfig ? (
              <Tag key={b} color={bossConfig.color} style={{ fontSize: 9, margin: 0 }}>{bossConfig.label}</Tag>
            ) : <Tag key={b}>{b}</Tag>;
          })}
        </Space>
      )
    },
    { 
      title: '比例', 
      dataIndex: 'ratio', 
      width: 70, 
      align: 'center',
      render: (v, record) => (
        <InputNumber 
          size="small"
          min={0}
          max={100}
          step={0.1}
          value={v ?? 1}
          style={{ width: 55 }}
          disabled={!(isCurrentUserHost || isAdmin)}
          onChange={(val) => updateCharRatio(record.characterName, val || 0)}
        />
      )
    },
    { 
      title: 'Mule', 
      dataIndex: 'isMule', 
      width: 60, 
      align: 'center',
      render: (v, record) => (
        <Checkbox 
          checked={v || false}
          disabled={!(isCurrentUserHost || isAdmin)}
          onChange={(e) => updateCharMule(record.characterName, e.target.checked)}
        />
      )
    },
    { 
      title: 'Meso', 
      dataIndex: 'meso', 
      width: 110, 
      render: (v) => <Text style={{ color: '#52c41a' }}>{(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Coin', 
      dataIndex: 'coin', 
      width: 80, 
      render: (v, record) => (
        <Text style={{ color: record.isMule ? '#d9d9d9' : '#faad14' }}>
          {record.isMule ? '-' : (v || 0)}
        </Text>
      )
    }
  ], [characters, isCurrentUserHost, isAdmin, updateCharRatio, updateCharMule]);

  // 检查是否可以完成（至少有角色且每个角色都选择了至少一个 Boss）
  const canComplete = useMemo(() => {
    if (characters.length === 0) return false;
    return characters.every(c => c.participatedBosses && c.participatedBosses.length > 0);
  }, [characters]);

  // 用户角色选项
  const userCharacterOptions = useMemo(() => {
    if (!currentUser?.characters) return [];
    const existingNames = characters.map(c => c.name.toLowerCase());
    return currentUser.characters
      .filter(char => !existingNames.includes(char.toLowerCase()))
      .map(char => ({
        value: char,
        label: (
          <Space>
            <img 
              src={`${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(char)}`}
              alt=""
              style={{ width: 24, height: 24, borderRadius: 4 }}
              onError={e => e.target.style.display = 'none'}
            />
            {char}
            <Tag color="green" style={{ fontSize: 10 }}>已绑定</Tag>
          </Space>
        )
      }));
  }, [currentUser?.characters, characters]);

  return (
    <div style={{ padding: '0 24px' }}>
      {contextHolder}
      
      {/* 历史记录选择器 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Space wrap>
            <Text strong><HistoryOutlined /> 历史记录:</Text>
            <Select
              placeholder="选择历史记录查看"
              style={{ width: 300 }}
              value={isHistoryView ? currentRunId : undefined}
              onChange={(value) => {
                if (value) {
                  handleLoadHistory(value);
                }
              }}
              allowClear
              onClear={() => { setIsHistoryView(false); loadFromServer(); }}
              showSearch
              optionFilterProp="children"
            >
              {historyList.map(h => (
                <Option key={h.runId} value={h.runId}>
                  {h.runName} - {new Date(h.completedAt).toLocaleDateString()} ({h.characterCount}人)
                </Option>
              ))}
            </Select>
            {isHistoryView && (
              <Button size="small" onClick={() => { setIsHistoryView(false); loadFromServer(); }}>
                返回当前
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* Run 选择器 */}
      {!isHistoryView && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <Space wrap>
              <Text strong>进行中的 Boss Rush ({runs.length}/{maxRuns}):</Text>
              {runs.map(run => (
                <Tag
                  key={run.id}
                  color={run.id === currentRunId ? 'green' : 'default'}
                  style={{ cursor: 'pointer', padding: '4px 12px' }}
                  onClick={() => switchRun(run.id)}
                >
                  <Tag color="#52c41a" style={{ marginRight: 4 }}>NT</Tag>
                  {run.name}
                  <Badge count={run.characters?.length || 0} style={{ marginLeft: 8 }} />
                </Tag>
              ))}
              {runs.length < maxRuns && (
                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                  新建 Boss Rush
                </Button>
              )}
            </Space>
            {currentRun && (isCurrentUserHost || isAdmin) && (
              <Popconfirm title="确定删除此 Run？" onConfirm={handleDeleteRun} okText="删除" cancelText="取消">
                <Button size="small" danger icon={<DeleteOutlined />}>删除当前 Run</Button>
              </Popconfirm>
            )}
          </div>
        </Card>
      )}

      {/* 当前 Run 信息 */}
      {currentRun && (
        <Card size="small" style={{ marginBottom: 16, textAlign: 'center' }}>
          <Space>
            <Tag color="#52c41a">NT</Tag>
            <Title level={5} style={{ margin: 0 }}>{isHistoryView ? historyViewName : currentRun.name}</Title>
            {isHistoryView && <Tag color="blue">历史记录</Tag>}
            {!isHistoryView && (isCurrentUserHost || isAdmin) && (
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditRunNameVisible(true)} />
            )}
          </Space>
        </Card>
      )}

      {/* 操作栏 */}
      {currentRun && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {!isHistoryView && (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCharacterModalVisible(true)}>
                添加角色
              </Button>
              {(isCurrentUserHost || isAdmin) && (
                <>
                  <Button icon={<TeamOutlined />} onClick={() => setPresetManageVisible(true)} loading={applyingPreset}>
                    预设小队
                  </Button>
                  <Button icon={<CheckCircleOutlined />} onClick={() => setBatchModalVisible(true)}>
                    批量更新参与 Boss
                  </Button>
                </>
              )}
              <Button 
                icon={<SaveOutlined />} 
                disabled={!canComplete || !(isCurrentUserHost || isAdmin)} 
                onClick={() => setCompleteModalVisible(true)}
                type={canComplete && (isCurrentUserHost || isAdmin) ? 'primary' : 'default'}
                danger={canComplete && (isCurrentUserHost || isAdmin)}
              >
                完成运行
              </Button>
            </>
          )}
          <Button icon={<SyncOutlined spin={syncing} />} onClick={loadFromServer} loading={loading}>刷新</Button>
        </div>
      )}

      {/* 角色列表 */}
      {currentRun && (
        <Card title="参与角色" size="small">
          {characters.length === 0 ? (
            <Empty description="暂无角色" />
          ) : (
            <Table
              dataSource={characters}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                {
                  title: '角色',
                  dataIndex: 'name',
                  key: 'name',
                  width: 120,
                  render: (name, record) => (
                    <Space>
                      <Text strong>{name}</Text>
                      {record.isLeader && <CrownOutlined style={{ color: '#faad14' }} />}
                    </Space>
                  )
                },
                // 按 Boss 名称分组，每组 2 列（R1, R2），Bga 只有 R1
                ...['Berga', 'Dunas1', 'Nib', 'Nameless', 'Dunas2', 'Core', 'Bga', 'Auf'].map(bossName => ({
                  title: bossName,
                  key: bossName,
                  width: bossName === 'Bga' ? 45 : 70,
                  align: 'center',
                  render: (_, record) => {
                    const r1Key = `${bossName}-R1`;
                    const r2Key = `${bossName}-R2`;
                    const bosses = record.participatedBosses || [];
                    const hasR1 = bosses.includes(r1Key);
                    const hasR2 = bosses.includes(r2Key);
                    const hasR2Option = bossName !== 'Bga'; // Bga 没有 R2
                    
                    if (isHistoryView) {
                      return (
                        <Space direction="vertical" size={0} style={{ fontSize: 10 }}>
                          {hasR1 && <Tag color={BOSS_RUSH_BOSSES.find(o => o.value === r1Key)?.color} style={{ margin: 0, fontSize: 9 }}>R1</Tag>}
                          {hasR2Option && hasR2 && <Tag color={BOSS_RUSH_BOSSES.find(o => o.value === r2Key)?.color} style={{ margin: 0, fontSize: 9 }}>R2</Tag>}
                        </Space>
                      );
                    }
                    
                    const toggleBoss = (bossKey) => {
                      const newBosses = bosses.includes(bossKey) 
                        ? bosses.filter(b => b !== bossKey)
                        : [...bosses, bossKey];
                      handleBossesChange(record.id, newBosses);
                    };
                    
                    return (
                      <Space direction="vertical" size={0}>
                        <Button 
                          size="small" 
                          type={hasR1 ? 'primary' : 'default'}
                          style={{ width: 36, height: 20, fontSize: 10, padding: 0 }}
                          onClick={() => toggleBoss(r1Key)}
                        >
                          R1
                        </Button>
                        {hasR2Option && (
                          <Button 
                            size="small" 
                            type={hasR2 ? 'primary' : 'default'}
                            style={{ width: 36, height: 20, fontSize: 10, padding: 0 }}
                            onClick={() => toggleBoss(r2Key)}
                          >
                            R2
                          </Button>
                        )}
                      </Space>
                    );
                  }
                })),
                {
                  title: '',
                  key: 'action',
                  width: 60,
                  render: (_, record) => !isHistoryView && (
                    <Popconfirm title="确定删除？" onConfirm={() => handleDeleteCharacter(record.id)}>
                      <Button type="link" size="small" danger>删除</Button>
                    </Popconfirm>
                  )
                }
              ]}
            />
          )}
        </Card>
      )}

      {/* 掉落物品 */}
      {(currentRun || isHistoryView) && (
        <Card 
          title={
            <Space>
              <GiftOutlined /> 掉落物品
              <Text type="secondary">总计: {lootSummary.meso.toLocaleString()} Meso / {lootSummary.coin} Coin</Text>
            </Space>
          }
          style={{ marginTop: 16 }}
          size="small"
          extra={!isHistoryView && (
            <Button type="primary" icon={<SaveOutlined />} onClick={saveLootData}>
              保存掉落数据
            </Button>
          )}
        >
          {!isHistoryView ? (
          <>
          <Form form={lootForm} layout="inline" size="small" style={{ marginBottom: 8 }} onFinish={(v) => { if (v.item && v.boss) { handleAddLoot(v.item.trim(), v.qty || 1, v.boss); lootForm.resetFields(); } }}>
            <Form.Item name="item" style={{ marginBottom: 0 }} rules={[{ required: true, message: '请输入物品名' }]}>
              <AutoComplete
                placeholder="输入或选择物品"
                style={{ width: 180 }}
                options={filteredLootItems.map(item => ({
                  value: item.name,
                  label: (
                    <Space>
                      <span>{item.name}</span>
                      {(item.mesoValue > 0 || item.coinValue > 0) && (
                        <Tag color="green" style={{ fontSize: 10 }}>
                          {item.coinValue > 0 ? `${item.coinValue}C` : `${(item.mesoValue || 0).toLocaleString()}M`}
                        </Tag>
                      )}
                    </Space>
                  )
                }))}
                filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
            <Form.Item name="boss" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
              <Select placeholder="Boss+Round" style={{ width: 140 }} allowClear>
                {BOSS_RUSH_BOSSES.map(o => (
                  <Option key={o.value} value={o.value}>
                    <Tag color={o.color} style={{ fontSize: 10, margin: 0 }}>{o.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="qty" initialValue={1} style={{ marginBottom: 0 }}>
              <InputNumber min={1} max={99} style={{ width: 60 }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>添加</Button>
            </Form.Item>
          </Form>
          <Table
            dataSource={lootItems}
            rowKey={(record) => record.boss ? `${record.name}_${record.boss}` : record.name}
            size="small"
            pagination={false}
            locale={{ emptyText: '无掉落物品' }}
            columns={[
              { title: '物品', dataIndex: 'name', ellipsis: true, width: 120 },
              { 
                title: '来源', 
                dataIndex: 'boss', 
                width: 100,
                render: (boss) => {
                  if (!boss) return <Text type="secondary">-</Text>;
                  const bossConfig = BOSS_RUSH_BOSSES.find(b => b.value === boss);
                  return bossConfig ? (
                    <Tag color={bossConfig.color} style={{ fontSize: 10, margin: 0 }}>{bossConfig.label}</Tag>
                  ) : boss;
                }
              },
              { 
                title: '数量', 
                dataIndex: 'quantity', 
                width: 70,
                render: (v, record) => (
                  <InputNumber 
                    size="small" 
                    min={1} 
                    value={v} 
                    style={{ width: 50 }}
                    onChange={(newVal) => {
                      if (newVal > 0) {
                        const key = record.boss ? `${record.name}_${record.boss}` : record.name;
                        setLootItems(prev => prev.map(item => 
                          (item.boss ? `${item.name}_${item.boss}` : item.name) === key 
                            ? { ...item, quantity: newVal } 
                            : item
                        ));
                      }
                    }}
                  />
                )
              },
              {
                title: '销售方式',
                dataIndex: 'sellMethod',
                width: 100,
                render: (v, record) => {
                  const itemKey = record.boss ? `${record.name}_${record.boss}` : record.name;
                  return (
                    <Select
                      size="small"
                      value={v || 'NPC'}
                      style={{ width: 80 }}
                      onChange={(newVal) => handleUpdateSellMethod(itemKey, newVal)}
                    >
                      <Option value="NPC">NPC</Option>
                      <Option value="FM">FM</Option>
                      <Option value="Others">其他</Option>
                    </Select>
                  );
                }
              },
              { 
                title: '价值', 
                width: 180,
                render: (_, r) => {
                  const itemConfig = lootConfig.items?.find(i => i.name === r.name);
                  const qty = r.quantity || 1;
                  
                  if (r.sellMethod === 'NPC') {
                    // NPC 价格：有 config 价格则展示，否则可编辑（NPC 只能 Meso）
                    const itemKey = r.boss ? `${r.name}_${r.boss}` : r.name;
                    const hasConfigPrice = itemConfig && (itemConfig.mesoValue > 0);
                    if (hasConfigPrice) {
                      return <Text style={{ color: '#52c41a' }}>{(itemConfig.mesoValue * qty).toLocaleString()} Meso</Text>;
                    }
                    // 无 config 价格，显示可编辑输入框（纯 Meso，不含 M 单位）
                    return (
                      <Space size={4}>
                        <InputNumber
                          size="small" min={0}
                          value={r.customMeso || 0}
                          style={{ width: 120 }}
                          formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={v => v.replace(/,/g, '')}
                          onChange={(val) => {
                            handleUpdateNpcPrice(itemKey, val || 0, 0);
                          }}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>Meso</Text>
                      </Space>
                    );
                  } else {
                    // 自定义价格（单个输入框 + 货币选择）
                    const itemKey = r.boss ? `${r.name}_${r.boss}` : r.name;
                    const currencyType = r.currencyType || (r.customCoin > 0 ? 'coin' : 'meso');
                    const customValue = currencyType === 'coin' ? (r.customCoin || 0) : (r.customMeso || 0);
                    return (
                      <Space.Compact size="small">
                        <InputNumber
                          size="small"
                          min={0}
                          value={customValue}
                          style={{ width: 100 }}
                          formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={v => v.replace(/,/g, '')}
                          onChange={(val) => {
                            if (currencyType === 'coin') {
                              handleUpdateSellMethod(itemKey, r.sellMethod, 0, val, 'coin');
                            } else {
                              handleUpdateSellMethod(itemKey, r.sellMethod, val, 0, 'meso');
                            }
                          }}
                        />
                        <Select 
                          size="small" 
                          value={currencyType}
                          style={{ width: 75 }}
                          onChange={(val) => {
                            if (val === 'coin') {
                              handleUpdateSellMethod(itemKey, r.sellMethod, 0, customValue || 0, 'coin');
                            } else {
                              handleUpdateSellMethod(itemKey, r.sellMethod, customValue || 0, 0, 'meso');
                            }
                          }}
                        >
                          <Option value="meso">Meso</Option>
                          <Option value="coin">Coin</Option>
                        </Select>
                      </Space.Compact>
                    );
                  }
                }
              },
              { 
                title: '', 
                width: 40,
                render: (_, r) => {
                  const itemKey = r.boss ? `${r.name}_${r.boss}` : r.name;
                  return (
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteLoot(itemKey)} />
                  );
                }
              }
            ]}
          />
          </>
          ) : (
            /* 历史视图只读模式 */
            <Table
              dataSource={lootItems}
              rowKey={(record) => record.boss ? `${record.name}_${record.boss}` : record.name}
              size="small"
              pagination={false}
              locale={{ emptyText: '无掉落物品记录' }}
              columns={[
                { title: '物品', dataIndex: 'name', ellipsis: true, width: 120 },
                { 
                  title: '来源', 
                  dataIndex: 'boss', 
                  width: 100,
                  render: (boss) => {
                    if (!boss) return <Text type="secondary">-</Text>;
                    const bossConfig = BOSS_RUSH_BOSSES.find(b => b.value === boss);
                    return bossConfig ? (
                      <Tag color={bossConfig.color} style={{ fontSize: 10, margin: 0 }}>{bossConfig.label}</Tag>
                    ) : boss;
                  }
                },
                { title: '数量', dataIndex: 'quantity', width: 60 },
                { title: '销售', dataIndex: 'sellMethod', width: 60 },
                { 
                  title: '价值', 
                  width: 100,
                  render: (_, r) => {
                    if (r.sellMethod === 'NPC') {
                      const itemConfig = lootConfig.items?.find(i => i.name === r.name);
                      if (!itemConfig || !itemConfig.mesoValue) return '-';
                      const qty = r.quantity || 1;
                      return <Text style={{ color: '#52c41a' }}>{(itemConfig.mesoValue * qty).toLocaleString()} Meso</Text>;
                    } else {
                      const currencyType = r.currencyType || (r.customCoin > 0 ? 'coin' : 'meso');
                      const customValue = currencyType === 'coin' ? (r.customCoin || 0) : (r.customMeso || 0);
                      return <Text style={{ color: currencyType === 'coin' ? '#faad14' : '#52c41a' }}>
                        {customValue.toLocaleString()} {currencyType === 'coin' ? 'Coin' : 'Meso'}
                      </Text>;
                    }
                  }
                }
              ]}
            />
          )}
        </Card>
      )}

      {/* 库存卡片 - 全局唯一，不随 Run 变化。Run 完成后该 Run 的掉落自动入库 */}
      <Card 
        title={
          <Space>
            <InboxOutlined /> 库存
            {isAdmin && (
              <Select
                size="small"
                value={selectedLeader || '__self__'}
                onChange={(v) => setSelectedLeader(v === '__self__' ? null : v)}
                style={{ width: 180 }}
                getPopupContainer={() => document.body}
              >
                <Option value="__self__">仅自己</Option>
                <Option value="__all__">全部 Leader</Option>
                {(inventory.allLeaders || []).map(l => (
                  <Option key={l} value={l}>{l}</Option>
                ))}
              </Select>
            )}
            {!isAdmin && currentUser?.username && (
              <Tag color="blue">Leader: {currentUser.username}</Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              共 {inventory.items?.length || 0} 种 / 总数 {(inventory.items || []).reduce((s, it) => s + (it.quantity || 0), 0)}
            </Text>
            {inventory.lastUpdated && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                · 更新于 {new Date(inventory.lastUpdated).toLocaleString()}
              </Text>
            )}
            <Button icon={<ReloadOutlined />} size="small" onClick={loadInventory} loading={inventoryLoading}>刷新</Button>
            {canManageInventory && (
              <Button icon={<PlusOutlined />} size="small" type="primary" onClick={() => setInventoryAddVisible(true)}>添加物品</Button>
            )}
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Boss Rush 完成后，该 Run 的掉落物品会自动累加到库存中"
          style={{ marginBottom: 12 }}
        />
        {(inventory.items || []).length === 0 ? (
          <Empty description="库存为空" />
        ) : (
          <>
            {groupedInventory.auf.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, padding: '6px 12px', background: '#fff7e6', borderLeft: '3px solid #fa8c16', borderRadius: '0 6px 6px 0' }}>
                  <Text strong style={{ color: '#d46b08' }}>Auf 物品</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    共 {groupedInventory.auf.reduce((s, g) => s + g.totalQty, 0)} 个 / {groupedInventory.auf.length} 种
                  </Text>
                </div>
                <InventoryGroupTable
                  groups={groupedInventory.auf}
                  canManageInventory={canManageInventory}
                  updateInventoryMeta={updateInventoryMeta}
                  adjustInventoryById={adjustInventoryById}
                  setInventoryItem={setInventoryItem}
                  deleteInventoryItem={deleteInventoryItem}
                  onSell={(item) => { setSellingItem(item); setSellModalVisible(true); }}
                  onBatchSell={(batch) => { setSellingItem({ ...batch, isBatch: true }); setSellModalVisible(true); }}
                />
              </div>
            )}

            {groupedInventory.other.length > 0 && (
              <div>
                {groupedInventory.auf.length > 0 && <Divider style={{ margin: '12px 0' }} />}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, padding: '6px 12px', background: '#f0f5ff', borderLeft: '3px solid #1890ff', borderRadius: '0 6px 6px 0' }}>
                  <Text strong style={{ color: '#096dd9' }}>其他物品</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    共 {inventory.items.filter(i => !isAufItem(i)).reduce((s, i) => s + (i.quantity || 0), 0)} 个 / {inventory.items.filter(i => !isAufItem(i)).length} 条
                  </Text>
                </div>
                <Table
                  dataSource={inventory.items.filter(i => !isAufItem(i))}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  loading={inventoryLoading}
                  scroll={{ x: 'max-content' }}
                  expandable={{
                    expandedRowRender: (record) => {
                      const src = record.source || {};
                      return (
                        <div style={{ paddingLeft: 24 }}>
                          <Space direction="vertical" size={4}>
                            <Space><Text type="secondary">Leader：</Text><Tag color="cyan">{record.leader || '-'}</Tag></Space>
                            <Space><Text type="secondary">入库时间：</Text><Text>{src.addedAt ? new Date(src.addedAt).toLocaleString() : '-'}</Text></Space>
                            <Space><Text type="secondary">来源 Run：</Text><Text>{src.runName || '-'}</Text>{src.boss && <Tag>{src.boss}</Tag>}</Space>
                            <Space wrap><Text type="secondary">参与角色：</Text>{(src.participants || []).length === 0 ? <Text type="secondary">无</Text> : (src.participants || []).map(p => <Tag key={p} icon={<UserOutlined />}>{p}</Tag>)}</Space>
                            {src.addedBy && <Space><Text type="secondary">操作员：</Text><Text>{src.addedBy}</Text></Space>}
                          </Space>
                        </div>
                      );
                    },
                    rowExpandable: () => true,
                  }}
                  columns={[
                    {
                      title: '物品', dataIndex: 'name', width: 220,
                      render: (name, record) => (
                        <Space direction="vertical" size={0}>
                          <Text strong style={{ whiteSpace: 'nowrap' }}>{name}</Text>
                          {record.source?.runName && (
                            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }} ellipsis={{ tooltip: `${record.source.runName}${record.source.boss ? ' · ' + record.source.boss : ''}` }}>
                              {record.source.runName}{record.source.boss && ` · ${record.source.boss}`}
                            </Text>
                          )}
                        </Space>
                      )
                    },
                    { title: '数量', dataIndex: 'quantity', width: 50, align: 'center', render: (q) => <Text strong style={{ color: '#1677ff' }}>{q}</Text> },
                    {
                      title: '价格 & 销售渠道', key: 'meta', width: 460,
                      render: (_, record) => canManageInventory ? (
                        <InventoryRowEditor record={record} onSave={updateInventoryMeta} />
                      ) : (
                        <Space>
                          <Text style={{ color: '#52c41a' }}>{Number(record.mesoValue || 0).toLocaleString()} M</Text>
                          <Text style={{ color: '#faad14' }}>{record.coinValue || 0} C</Text>
                          {record.sellMethod ? <Tag color={record.sellMethod === 'NPC' ? 'blue' : record.sellMethod === 'FM' ? 'purple' : 'default'}>{record.sellMethod}</Tag> : <Text type="secondary">渠道未设置</Text>}
                        </Space>
                      )
                    },
                    ...(canManageInventory ? [{
                      title: '操作', width: 320,
                      render: (_, record) => (
                        <Space>
                          {record.quantity === 1 ? (
                            <Popconfirm title={`库存只剩 1 个，确认减为 0 并移除该批次 ${record.name}？`} onConfirm={() => adjustInventoryById(record.id, record.quantity, -1)} okText="确定" cancelText="取消" okButtonProps={{ danger: true }}>
                              <Button size="small" icon={<MinusOutlined />} danger />
                            </Popconfirm>
                          ) : (
                            <Button size="small" icon={<MinusOutlined />} onClick={() => adjustInventoryById(record.id, record.quantity, -1)} disabled={record.quantity <= 0} />
                          )}
                          <Button size="small" icon={<PlusOutlined />} onClick={() => adjustInventoryById(record.id, record.quantity, 1)} />
                          <InputNumber size="small" min={0} style={{ width: 60 }} value={record.quantity}
                            onPressEnter={(e) => setInventoryItem(record.id, parseInt(e.target.value) || 0)}
                            onBlur={(e) => { const v = parseInt(e.target.value) || 0; if (v !== record.quantity) setInventoryItem(record.id, v); }}
                          />
                          <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => { setSellingItem(record); setSellModalVisible(true); }} disabled={record.quantity <= 0}>售出</Button>
                          <Popconfirm title={`删除该批次 ${record.name}？`} onConfirm={() => deleteInventoryItem(record.id)} okText="确定" cancelText="取消">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      )
                    }] : [])
                  ]}
                />
              </div>
            )}

            {/* 汇总 */}
            <div style={{ marginTop: 12, padding: '8px 16px', background: 'var(--surface-subtle)', borderRadius: 8, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
              <Text strong>合计</Text>
              <Text>总量: <Tag color="blue">{inventory.items.reduce((s, it) => s + (it.quantity || 0), 0)}</Tag></Text>
              <Text>Meso: <Text style={{ color: '#52c41a' }}>{inventory.items.reduce((s, it) => s + (Number(it.mesoValue) || 0) * (it.quantity || 0), 0).toLocaleString()}</Text></Text>
              <Text>Coin: <Text style={{ color: '#faad14' }}>{inventory.items.reduce((s, it) => s + (Number(it.coinValue) || 0) * (it.quantity || 0), 0)}</Text></Text>
            </div>
          </>
        )}
      </Card>


      {/* 已售出记录卡片 - 全局唯一 */}
      <Card
        title={<><DollarOutlined /> 已售出记录</>}
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              共 {sales.length} 笔 / 总 Meso {sales.reduce((s, x) => s + (Number(x.mesoAmount) || 0), 0).toLocaleString()} / 总 Coin {sales.reduce((s, x) => s + (Number(x.coinAmount) || 0), 0)}
            </Text>
            <Button icon={<ReloadOutlined />} size="small" onClick={loadSales} loading={salesLoading}>刷新</Button>
          </Space>
        }
      >
        {sales.length === 0 ? (
          <Empty description="暂无售出记录" />
        ) : (
          <>
            {/* 待分配汇总：跨所有未分配售出记录，按受益人合计 */}
            <Card
              size="small"
              type="inner"
              title={<><CalculatorOutlined /> 待分配汇总（全部未分配售出按人合计）</>}
              style={{ marginBottom: 12 }}
            >
              {(() => {
                const agg = {};  // {name: {meso, coin, count}}
                sales.forEach(s => {
                  (s.beneficiaries || []).forEach(b => {
                    if (b.distributed) return; // 已分配的不计入
                    const name = b.name || 'Unknown';
                    if (!agg[name]) agg[name] = { meso: 0, coin: 0, count: 0 };
                    agg[name].meso += Number(b.meso) || 0;
                    agg[name].coin += Number(b.coin) || 0;
                    agg[name].count += 1;
                  });
                });
                const rows = Object.entries(agg)
                  .map(([name, v]) => ({ name, ...v }))
                  .sort((a, b) => b.meso - a.meso);

                if (rows.length === 0) {
                  return <Text type="secondary">无受益人记录</Text>;
                }

                const totalMeso = rows.reduce((s, r) => s + r.meso, 0);
                const totalCoin = rows.reduce((s, r) => s + r.coin, 0);

                return (
                  <Table
                    dataSource={rows}
                    rowKey="name"
                    pagination={false}
                    size="small"
                    columns={[
                      { 
                        title: '角色', 
                        dataIndex: 'name', 
                        render: n => <Tag icon={<UserOutlined />} color="blue">{n}</Tag>
                      },
                      { 
                        title: '应分 Meso', 
                        dataIndex: 'meso', 
                        align: 'right',
                        sorter: (a, b) => a.meso - b.meso,
                        render: v => <Text strong style={{ color: '#52c41a' }}>{Number(v).toLocaleString()}</Text>
                      },
                      { 
                        title: '应分 Coin', 
                        dataIndex: 'coin', 
                        align: 'right',
                        sorter: (a, b) => a.coin - b.coin,
                        render: v => <Text strong style={{ color: '#faad14' }}>{v}</Text>
                      },
                      { 
                        title: '涉及笔数', 
                        dataIndex: 'count', 
                        align: 'center',
                        width: 90,
                        render: c => <Tag>{c}</Tag>
                      },
                      ...(canManageInventory ? [{
                        title: '操作',
                        width: 100,
                        align: 'center',
                        render: (_, record) => (
                          <Popconfirm
                            title={`标记 ${record.name} 已分配？`}
                            description="该角色在所有售出记录中标记为已分配；若某条售出所有人均已分配则移除"
                            onConfirm={() => distributeBeneficiary(record.name)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button size="small" type="primary" icon={<CheckCircleOutlined />}>已分配</Button>
                          </Popconfirm>
                        )
                      }] : []),
                    ]}
                    summary={() => (
                      <Table.Summary.Row style={{ background: 'var(--surface-subtle)', fontWeight: 'bold' }}>
                        <Table.Summary.Cell index={0}>合计 ({rows.length} 人)</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text strong style={{ color: '#52c41a' }}>{totalMeso.toLocaleString()}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <Text strong style={{ color: '#faad14' }}>{totalCoin}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} />
                        {canManageInventory && <Table.Summary.Cell index={4} />}
                      </Table.Summary.Row>
                    )}
                  />
                );
              })()}
            </Card>

          <Table
            dataSource={sales}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: false }}
            loading={salesLoading}
            expandable={{
              expandedRowRender: (record) => {
                const bs = record.beneficiaries || [];
                if (bs.length === 0) return <Text type="secondary" style={{ paddingLeft: 24 }}>无受益人记录</Text>;
                return (
                  <Table
                    dataSource={bs}
                    rowKey={(_, idx) => `${record.id}-b-${idx}`}
                    size="small"
                    pagination={false}
                    columns={[
                      { title: '角色', dataIndex: 'name', render: n => <Tag icon={<UserOutlined />}>{n}</Tag> },
                      { title: '获得 Meso', dataIndex: 'meso', render: v => <Text style={{ color: '#52c41a' }}>{Number(v || 0).toLocaleString()}</Text> },
                      { title: '获得 Coin', dataIndex: 'coin', render: v => <Text style={{ color: '#faad14' }}>{v || 0}</Text> },
                    ]}
                  />
                );
              },
              rowExpandable: (record) => (record.beneficiaries || []).length > 0,
            }}
            columns={[
              { title: '售出时间', dataIndex: 'soldAt', width: 160, render: t => t ? new Date(t).toLocaleString() : '-' },
              ...(isAdmin && (selectedLeader === '__all__' || !selectedLeader) ? [
                { title: 'Leader', dataIndex: 'leader', width: 100, render: l => <Tag color="cyan">{l || '-'}</Tag> }
              ] : []),
              { title: '物品', dataIndex: 'itemName', render: n => <Text strong>{n}</Text> },
              { title: '数量', dataIndex: 'quantity', width: 70, render: q => <Tag color="blue">{q}</Tag> },
              {
                title: '成交金额', width: 260,
                render: (_, r) => (
                  <Space direction="vertical" size={0}>
                    <Space size={4}>
                      <Text style={{ color: '#52c41a' }}>{Number(r.mesoAmount || 0).toLocaleString()} M</Text>
                      <Text type="secondary">/</Text>
                      <Text style={{ color: '#faad14' }}>{r.coinAmount || 0} C</Text>
                    </Space>
                    {r.taxAmount > 0 && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        税前 {Number(r.grossMesoAmount || r.mesoAmount + r.taxAmount || 0).toLocaleString()} M，扣税 {Number(r.taxAmount || 0).toLocaleString()} M (2.5%)
                      </Text>
                    )}
                  </Space>
                )
              },
              { 
                title: '渠道', dataIndex: 'sellMethod', width: 90,
                render: v => v ? <Tag color={v === 'NPC' ? 'blue' : v === 'FM' ? 'purple' : 'default'}>{v}</Tag> : <Text type="secondary">-</Text>
              },
              { 
                title: '受益人', width: 200, dataIndex: 'beneficiaries',
                render: (bs) => (
                  <Space wrap size={[4, 4]}>
                    {(bs || []).length === 0 ? <Text type="secondary">-</Text>
                      : (bs || []).map((b, i) => <Tag key={i} icon={<UserOutlined />}>{b.name}</Tag>)}
                  </Space>
                )
              },
              { title: '操作员', dataIndex: 'soldBy', width: 100, render: u => <Text type="secondary">{u || '-'}</Text> },
              { title: '备注', dataIndex: 'note', render: n => n ? <Text>{n}</Text> : <Text type="secondary">-</Text> },
              ...(canManageInventory ? [{
                title: '操作', width: 180,
                render: (_, r) => (
                  <Space>
                    <Popconfirm
                      title="标记为已分配？"
                      description="该笔售出从列表移除（不影响库存）"
                      onConfirm={() => distributeSale(r.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button size="small" type="primary" icon={<CheckCircleOutlined />}>已分配</Button>
                    </Popconfirm>
                    <Popconfirm
                      title="撤销该售出记录？"
                      description="物品将退回库存"
                      onConfirm={() => revokeSale(r.id)}
                      okText="确定"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" danger>撤销</Button>
                    </Popconfirm>
                  </Space>
                )
              }] : [])
            ]}
          />
          </>
        )}
      </Card>

      {/* 无 Run 时显示 */}
      {!currentRun && !loading && (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <Empty description="暂无进行中的 Boss Rush">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
              创建 Boss Rush
            </Button>
          </Empty>
        </Card>
      )}

      {/* 创建 Run 弹窗 */}
      <CreateBossRushModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={handleCreateRun}
        currentUser={currentUser}
        existingRuns={runs.length}
        maxRuns={maxRuns}
      />

      {/* 添加角色弹窗 */}
      <CharacterModal
        visible={characterModalVisible}
        onCancel={() => setCharacterModalVisible(false)}
        onSave={handleSaveCharacter}
        userCharacterOptions={userCharacterOptions}
      />

      {/* 批量更新参与 Boss 弹窗 */}
      <BatchUpdateModal
        visible={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        onConfirm={handleBatchUpdate}
        characters={characters}
      />

      {/* 预设小队管理弹窗 */}
      <PresetTeamManageModal
        visible={presetManageVisible}
        onCancel={() => setPresetManageVisible(false)}
        onApply={handleApplyPreset}
        currentUser={currentUser}
        isHost={isCurrentUserHost}
        isAdmin={isAdmin}
      />

      {/* 完成运行弹窗 */}
      <CompleteRunModal
        visible={completeModalVisible}
        onCancel={() => setCompleteModalVisible(false)}
        onConfirm={handleCompleteRun}
        defaultName={currentRun?.name}
      />

      {/* 编辑 Run 名称弹窗 */}
      <EditRunNameModal
        visible={editRunNameVisible}
        onCancel={() => setEditRunNameVisible(false)}
        onSuccess={(newName) => {
          setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, name: newName } : r));
          setEditRunNameVisible(false);
        }}
        currentRun={currentRun}
        currentUser={currentUser}
      />

      {/* 添加库存物品弹窗 */}
      <InventoryAddModal
        visible={inventoryAddVisible}
        onCancel={() => setInventoryAddVisible(false)}
        onConfirm={async (name, qty, participants) => {
          await addInventoryItem(name, qty, participants);
          setInventoryAddVisible(false);
        }}
        lootConfig={lootConfig}
        knownCharacters={knownCharacters}
      />

      {/* 售出库存弹窗 */}
      <SellInventoryModal
        visible={sellModalVisible}
        onCancel={() => { setSellModalVisible(false); setSellingItem(null); }}
        onConfirm={sellInventory}
        item={sellingItem}
        knownCharacters={knownCharacters}
      />
    </div>
  );
};

// 售出库存弹窗
const SellInventoryModal = ({ visible, onCancel, onConfirm, item, knownCharacters }) => {
  const [quantity, setQuantity] = useState(1);
  const [mesoAmount, setMesoAmount] = useState(0);
  const [coinAmount, setCoinAmount] = useState(0);
  const [sellMethod, setSellMethod] = useState(undefined);
  const [beneficiaries, setBeneficiaries] = useState([]);  // [{name, meso, coin}]
  const [note, setNote] = useState('');
  const [autoSplit, setAutoSplit] = useState(true);  // 自动均分
  const [submitting, setSubmitting] = useState(false);

  // 弹窗打开时根据 item 预填
  useEffect(() => {
    if (!visible || !item) return;
    setSellMethod(item.sellMethod || undefined);
    setNote('');

    if (item.isBatch) {
      // 批量售出：逐项按各自参与者计算分配，再按人汇总
      const totalQty = item.items.reduce((s, it) => s + (it.quantity || 0), 0);
      const totalMeso = item.items.reduce((s, it) => s + (it.mesoValue || 0) * (it.quantity || 0), 0);
      const totalCoin = item.items.reduce((s, it) => s + (it.coinValue || 0) * (it.quantity || 0), 0);
      setQuantity(totalQty);
      setMesoAmount(totalMeso);
      setCoinAmount(totalCoin);
      setSellMethod(item.items[0]?.sellMethod || undefined);

      // 逐项计算每个参与者的应得份额，再按人汇总
      const agg = {}; // { name: { meso, coin } }
      item.items.forEach(it => {
        const qty = it.quantity || 1;
        const itemMeso = (it.mesoValue || 0) * qty;
        const itemCoin = (it.coinValue || 0) * qty;
        const parts = (it.source?.participants || []).filter(Boolean);
        if (parts.length === 0) return;
        const mesoEach = Math.floor(itemMeso / parts.length);
        const mesoRem = itemMeso - mesoEach * parts.length;
        const coinEach = Math.floor(itemCoin / parts.length);
        const coinRem = itemCoin - coinEach * parts.length;
        parts.forEach((p, i) => {
          if (!agg[p]) agg[p] = { meso: 0, coin: 0 };
          agg[p].meso += mesoEach + (i < mesoRem ? 1 : 0);
          agg[p].coin += coinEach + (i < coinRem ? 1 : 0);
        });
      });
      setBeneficiaries(Object.entries(agg).map(([name, v]) => ({ name, meso: v.meso, coin: v.coin })));
      setAutoSplit(false);
    } else {
      setQuantity(1);
      setSellMethod(item.sellMethod || undefined);
      const defaultMeso = (item.mesoValue || 0) * 1;
      const defaultCoin = (item.coinValue || 0) * 1;
      setMesoAmount(defaultMeso);
      setCoinAmount(defaultCoin);
      const initialNames = (item.source?.participants || []).filter(Boolean);
      setBeneficiaries(initialNames.map(n => ({ name: n, meso: 0, coin: 0 })));
    }
    setAutoSplit(true);
  }, [visible, item]);

  // 自动均分（受益人数变化、总金额变化、autoSplit 开启时）
  useEffect(() => {
    if (!autoSplit || beneficiaries.length === 0) return;
    const n = beneficiaries.length;
    const baseMeso = Math.floor((mesoAmount || 0) / n);
    const remainderMeso = (mesoAmount || 0) - baseMeso * n;
    const baseCoin = Math.floor((coinAmount || 0) / n);
    const remainderCoin = (coinAmount || 0) - baseCoin * n;

    setBeneficiaries(prev => prev.map((b, i) => ({
      ...b,
      meso: baseMeso + (i < remainderMeso ? 1 : 0),
      coin: baseCoin + (i < remainderCoin ? 1 : 0)
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSplit, mesoAmount, coinAmount, beneficiaries.length]);

  const charOptions = useMemo(() => {
    return (knownCharacters || []).map(c => ({ value: c, label: c }));
  }, [knownCharacters]);

  const totalBenefMeso = useMemo(() => beneficiaries.reduce((s, b) => s + (Number(b.meso) || 0), 0), [beneficiaries]);
  const totalBenefCoin = useMemo(() => beneficiaries.reduce((s, b) => s + (Number(b.coin) || 0), 0), [beneficiaries]);
  const mesoMatched = Math.abs(totalBenefMeso - (mesoAmount || 0)) <= 1;
  const coinMatched = Math.abs(totalBenefCoin - (coinAmount || 0)) <= 0.01;

  // 当数量变化时，按单价自动调整成交金额
  const handleQtyChange = (v) => {
    const newQty = v || 1;
    setQuantity(newQty);
    if (item) {
      setMesoAmount((item.mesoValue || 0) * newQty);
      setCoinAmount((item.coinValue || 0) * newQty);
    }
  };

  const updateBenefName = (idx, name) => {
    setBeneficiaries(prev => prev.map((b, i) => i === idx ? { ...b, name } : b));
  };
  const updateBenefMeso = (idx, val) => {
    setAutoSplit(false);
    setBeneficiaries(prev => prev.map((b, i) => i === idx ? { ...b, meso: val || 0 } : b));
  };
  const updateBenefCoin = (idx, val) => {
    setAutoSplit(false);
    setBeneficiaries(prev => prev.map((b, i) => i === idx ? { ...b, coin: val || 0 } : b));
  };
  const addBenef = () => setBeneficiaries(prev => [...prev, { name: '', meso: 0, coin: 0 }]);
  const removeBenef = (idx) => setBeneficiaries(prev => prev.filter((_, i) => i !== idx));

  const handleOk = async () => {
    if (!item) return;
    if (!quantity || quantity <= 0) { message.warning('数量必须大于 0'); return; }
    if (!item.isBatch && quantity > item.quantity) { message.warning(`库存只有 ${item.quantity}，无法售出 ${quantity}`); return; }
    if (mesoAmount < 0 || coinAmount < 0) { message.warning('金额不能为负数'); return; }
    const validBenefs = beneficiaries.filter(b => b.name && b.name.trim());
    if (validBenefs.length === 0) { message.warning('至少添加一个受益人'); return; }
    if (!mesoMatched) { message.warning(`受益人 Meso 总额 ${totalBenefMeso} ≠ 售出 ${mesoAmount}`); return; }
    if (!coinMatched) { message.warning(`受益人 Coin 总额 ${totalBenefCoin} ≠ 售出 ${coinAmount}`); return; }

    const beneficiariesPayload = validBenefs.map(b => ({ name: b.name.trim(), meso: Number(b.meso) || 0, coin: Number(b.coin) || 0 }));

    setSubmitting(true);

    if (item.isBatch) {
      // 批量售出：逐项按各自参与者计算应得份额
      let allOk = true;
      for (const it of item.items) {
        const qty = it.quantity || 1;
        const itemMeso = (it.mesoValue || 0) * qty;
        const itemCoin = (it.coinValue || 0) * qty;
        const parts = (it.source?.participants || []).filter(Boolean);

        // 按该项的参与者均分该项的金额
        let itemBeneficiaries;
        if (parts.length > 0) {
          const mesoEach = Math.floor(itemMeso / parts.length);
          const mesoRem = itemMeso - mesoEach * parts.length;
          const coinEach = Math.floor(itemCoin / parts.length);
          const coinRem = itemCoin - coinEach * parts.length;
          itemBeneficiaries = parts.map((p, i) => ({
            name: p,
            meso: mesoEach + (i < mesoRem ? 1 : 0),
            coin: coinEach + (i < coinRem ? 1 : 0),
          }));
        } else {
          // 无参与者时均分给弹窗中所有受益人
          itemBeneficiaries = validBenefs.map(b => ({
            name: b.name,
            meso: Math.floor(itemMeso / validBenefs.length),
            coin: Math.floor(itemCoin / validBenefs.length),
          }));
        }

        const ok = await onConfirm({
          inventoryId: it.id,
          quantity: qty,
          mesoAmount: itemMeso,
          coinAmount: itemCoin,
          sellMethod: sellMethod || '',
          beneficiaries: itemBeneficiaries,
          itemName: it.name,
          note: note ? `${note}（批量售出）` : '批量售出'
        });
        if (!ok) { allOk = false; break; }
      }
      if (allOk) message.success(`批量售出完成：${item.items.length} 条 / ${quantity} 件`);
    } else {
      const ok = await onConfirm({
        inventoryId: item.id,
        quantity,
        mesoAmount: Number(mesoAmount) || 0,
        coinAmount: Number(coinAmount) || 0,
        sellMethod: sellMethod || '',
        beneficiaries: beneficiariesPayload,
        itemName: item.name,
        note: note || ''
      });
      if (!ok) { setSubmitting(false); return; }
    }

    setSubmitting(false);
    onCancel();
  };

  if (!item) return null;

  return (
    <Modal
      title={<><DollarOutlined /> 售出库存物品</>}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText="确认售出"
      cancelText="取消"
      confirmLoading={submitting}
      width={680}
    >
      <Form layout="vertical">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="物品">
              <Input value={item.isBatch ? `${item.name}（批量 ${item.items?.length || 0} 条）` : item.name} disabled />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={item.isBatch ? `数量（合计）` : `数量（库存 ${item.quantity}）`} required>
              <InputNumber
                min={1}
                max={item.isBatch ? undefined : item.quantity}
                value={quantity}
                onChange={item.isBatch ? setQuantity : handleQtyChange}
                style={{ width: '100%' }}
                precision={0}
                disabled={item.isBatch}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="销售渠道">
              <Select
                value={sellMethod}
                onChange={setSellMethod}
                allowClear
                placeholder="渠道"
                getPopupContainer={() => document.body}
                options={[
                  { value: 'NPC', label: 'NPC' },
                  { value: 'FM', label: 'FM' },
                  { value: 'Others', label: 'Others' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="成交 Meso 总额" required>
              <InputNumber
                min={0}
                value={mesoAmount}
                onChange={v => setMesoAmount(v ?? 0)}
                style={{ width: '100%' }}
                precision={0}
                formatter={x => `${x ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={x => x ? Number(`${x}`.replace(/,/g, '')) : 0}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="成交 Coin 总额" required>
              <InputNumber
                min={0}
                value={coinAmount}
                onChange={v => setCoinAmount(v ?? 0)}
                style={{ width: '100%' }}
                precision={0}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="备注">
          <Input.TextArea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="备注信息（可选）"
            maxLength={200}
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong>受益人分配</Text>
          <Space>
            <Checkbox checked={autoSplit} onChange={e => setAutoSplit(e.target.checked)}>
              自动均分
            </Checkbox>
            <Button size="small" icon={<PlusOutlined />} onClick={addBenef}>添加</Button>
          </Space>
        </div>

        <Table
          dataSource={beneficiaries}
          rowKey={(_, idx) => `b-${idx}`}
          pagination={false}
          size="small"
          locale={{ emptyText: '请添加受益人' }}
          columns={[
            {
              title: '角色',
              dataIndex: 'name',
              render: (name, _, idx) => (
                <AutoComplete
                  value={name}
                  onChange={v => updateBenefName(idx, v)}
                  options={charOptions}
                  filterOption={(input, option) => 
                    (option?.value || '').toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="角色名"
                  allowClear
                  style={{ width: '100%' }}
                />
              )
            },
            {
              title: 'Meso',
              dataIndex: 'meso',
              width: 140,
              render: (v, _, idx) => (
                <InputNumber
                  min={0}
                  precision={0}
                  value={v}
                  onChange={val => updateBenefMeso(idx, val)}
                  style={{ width: '100%' }}
                  formatter={x => `${x ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={x => x ? Number(`${x}`.replace(/,/g, '')) : 0}
                />
              )
            },
            {
              title: 'Coin',
              dataIndex: 'coin',
              width: 90,
              render: (v, _, idx) => (
                <InputNumber
                  min={0}
                  precision={0}
                  value={v}
                  onChange={val => updateBenefCoin(idx, val)}
                  style={{ width: '100%' }}
                />
              )
            },
            {
              title: '',
              width: 40,
              render: (_, __, idx) => (
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeBenef(idx)} />
              )
            }
          ]}
          summary={() => (
            <Table.Summary.Row style={{ background: 'var(--surface-subtle)' }}>
              <Table.Summary.Cell index={0}>
                <Text strong>合计</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <Text style={{ color: mesoMatched ? '#52c41a' : '#f5222d' }}>
                  {totalBenefMeso.toLocaleString()} / {Number(mesoAmount || 0).toLocaleString()}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}>
                <Text style={{ color: coinMatched ? '#52c41a' : '#f5222d' }}>
                  {totalBenefCoin} / {coinAmount || 0}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} />
            </Table.Summary.Row>
          )}
        />
        {(!mesoMatched || !coinMatched) && (
          <Alert
            type="warning"
            showIcon
            message="受益人分成总和与售出金额不一致"
            style={{ marginTop: 8 }}
          />
        )}
      </Form>
    </Modal>
  );
};

// 创建 Boss Rush 弹窗
const CreateBossRushModal = ({ visible, onCancel, onSuccess, currentUser, existingRuns, maxRuns }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  // 记录用户是否手动改过标题：改过就不再自动覆盖
  const [nameTouched, setNameTouched] = useState(false);

  // 生成默认标题：今天日期 + host 角色名，例如 "2026-07-16 张三"
  const buildDefaultName = useCallback((host) => {
    const d = new Date();
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return host ? `${date} ${host}` : date;
  }, []);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({
        roles: ['host', 'attacker'],
        isLeader: true,
        name: buildDefaultName('')
      });
      setSelectedPresetId(null);
      setNameTouched(false);
      // 加载预设小队列表
      if (currentUser?.username) {
        fetch(`${API_BASE_URL}/api/preset-teams?username=${encodeURIComponent(currentUser.username)}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setPresets(data.presets || []); })
          .catch(e => console.error(e));
      }
    }
  }, [visible, form, currentUser?.username, buildDefaultName]);

  // Host 角色名变化时，若用户未手动改过标题，则自动更新默认标题
  const handleHostChange = (host) => {
    if (!nameTouched) {
      form.setFieldsValue({ name: buildDefaultName((host || '').trim()) });
    }
  };

  // 选择预设小队：自动把第一个成员填为 Host，并同步默认标题
  const handlePresetChange = (presetId) => {
    setSelectedPresetId(presetId);
    if (!presetId) return;
    const preset = presets.find(p => p.id === presetId);
    const firstMember = (preset?.members || []).find(m => (m.name || '').trim());
    if (firstMember) {
      const host = firstMember.name.trim();
      form.setFieldsValue({ hostCharacter: host });
      if (!nameTouched) {
        form.setFieldsValue({ name: buildDefaultName(host) });
      }
    }
  };

  const selectedPreset = useMemo(() => {
    return presets.find(p => p.id === selectedPresetId) || null;
  }, [presets, selectedPresetId]);

  const userCharacterOptions = useMemo(() => {
    if (!currentUser?.characters) return [];
    return currentUser.characters.map(char => ({
      value: char,
      label: (
        <Space>
          <img
            src={`${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(char)}`}
            alt=""
            style={{ width: 24, height: 24, borderRadius: 4 }}
            onError={e => e.target.style.display = 'none'}
          />
          {char}
          <Tag color="green" style={{ fontSize: 10 }}>已绑定</Tag>
        </Space>
      )
    }));
  }, [currentUser?.characters]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSuccess({ ...values, presetId: selectedPresetId });
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<><ThunderboltOutlined /> 创建 Boss Rush</>}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText="创建"
      cancelText="取消"
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">当前已有 {existingRuns} / {maxRuns} 个 Boss Rush</Text>
      </div>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]} extra="默认按「日期 + Host 名」生成，可手动修改">
          <Input placeholder="例如：NT Boss Rush 周日场" onChange={() => setNameTouched(true)} />
        </Form.Item>
        <Alert message="创建时必须添加第一个 Host 角色" type="info" showIcon style={{ marginBottom: 16 }} />
        <Form.Item name="hostCharacter" label="Host 角色名" rules={[{ required: true, message: '请输入角色名' }]}>
          <AutoComplete
            options={userCharacterOptions}
            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
            onChange={handleHostChange}
          >
            <Input prefix={<UserOutlined />} placeholder="输入角色名" maxLength={12} />
          </AutoComplete>
        </Form.Item>
        {presets.length > 0 && (
          <>
            <Divider />
            <Form.Item label="预设小队（可选）">
              <Select
                placeholder="选择预设小队，自动导入成员并将第一人设为 Host"
                allowClear
                value={selectedPresetId}
                onChange={handlePresetChange}
              >
                {presets.map(p => (
                  <Option key={p.id} value={p.id}>
                    <Space>
                      <TeamOutlined />
                      <Text>{p.name}</Text>
                      <Tag>{(p.members || []).length}人</Tag>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {selectedPreset && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">成员预览：</Text>
                <div style={{ marginTop: 4 }}>
                  {(selectedPreset.members || []).map((m, i) => (
                    <Tag key={i} icon={<UserOutlined />} style={{ marginBottom: 4 }}>{m.name}</Tag>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

// 添加角色弹窗
const CharacterModal = ({ visible, onCancel, onSave, userCharacterOptions }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleOk = () => {
    form.validateFields().then(values => {
      onSave(values);
    });
  };

  return (
    <Modal
      title="添加角色"
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText="添加"
      cancelText="取消"
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="角色名" rules={[{ required: true, message: '请输入角色名' }]}>
          <AutoComplete
            options={userCharacterOptions}
            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
          >
            <Input prefix={<UserOutlined />} placeholder="输入角色名" maxLength={12} />
          </AutoComplete>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// 批量更新参与 Boss 弹窗
const BatchUpdateModal = ({ visible, onCancel, onConfirm, characters }) => {
  const [bossesMap, setBossesMap] = useState({});

  useEffect(() => {
    if (visible) {
      const initialMap = {};
      characters.forEach(c => { initialMap[c.id] = c.participatedBosses || []; });
      setBossesMap(initialMap);
    }
  }, [visible, characters]);

  // 快速全选某个 Boss
  const handleToggleAll = (bossValue) => {
    setBossesMap(prev => {
      const newMap = { ...prev };
      const allHave = characters.every(c => (prev[c.id] || []).includes(bossValue));
      characters.forEach(c => {
        const current = prev[c.id] || [];
        if (allHave) {
          newMap[c.id] = current.filter(b => b !== bossValue);
        } else {
          if (!current.includes(bossValue)) {
            newMap[c.id] = [...current, bossValue];
          }
        }
      });
      return newMap;
    });
  };

  const bossNames = ['Berga', 'Dunas1', 'Nib', 'Nameless', 'Dunas2', 'Core', 'Bga', 'Auf'];

  return (
    <Modal
      title="批量更新参与 Boss"
      open={visible}
      onCancel={onCancel}
      onOk={() => onConfirm(bossesMap)}
      okText="确定"
      cancelText="取消"
      width={800}
    >
      <div style={{ marginBottom: 16 }}>
        <Text>快速切换全部（点击按钮切换所有角色该 Boss 的状态）:</Text>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {BOSS_RUSH_BOSSES.map(o => (
            <Button key={o.value} size="small" onClick={() => handleToggleAll(o.value)}>
              <Tag color={o.color} style={{ fontSize: 10 }}>{o.label}</Tag>
            </Button>
          ))}
        </div>
      </div>
      <Table
        dataSource={characters}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ x: 700 }}
        columns={[
          { title: '角色', dataIndex: 'name', key: 'name', width: 100, fixed: 'left' },
          ...bossNames.map(bossName => ({
            title: bossName,
            key: bossName,
            width: bossName === 'Bga' ? 45 : 70,
            align: 'center',
            render: (_, record) => {
              const r1Key = `${bossName}-R1`;
              const r2Key = `${bossName}-R2`;
              const bosses = bossesMap[record.id] || [];
              const hasR1 = bosses.includes(r1Key);
              const hasR2 = bosses.includes(r2Key);
              const hasR2Option = bossName !== 'Bga'; // Bga 没有 R2
              
              const toggleBoss = (bossKey) => {
                const newBosses = bosses.includes(bossKey) 
                  ? bosses.filter(b => b !== bossKey)
                  : [...bosses, bossKey];
                setBossesMap(prev => ({ ...prev, [record.id]: newBosses }));
              };
              
              return (
                <Space direction="vertical" size={0}>
                  <Button 
                    size="small" 
                    type={hasR1 ? 'primary' : 'default'}
                    style={{ width: 36, height: 20, fontSize: 10, padding: 0 }}
                    onClick={() => toggleBoss(r1Key)}
                  >
                    R1
                  </Button>
                  {hasR2Option && (
                    <Button 
                      size="small" 
                      type={hasR2 ? 'primary' : 'default'}
                      style={{ width: 36, height: 20, fontSize: 10, padding: 0 }}
                      onClick={() => toggleBoss(r2Key)}
                    >
                      R2
                    </Button>
                  )}
                </Space>
              );
            }
          }))
        ]}
      />
    </Modal>
  );
};

// 完成运行弹窗
const CompleteRunModal = ({ visible, onCancel, onConfirm, defaultName }) => {
  const [runName, setRunName] = useState('');

  useEffect(() => {
    if (visible && defaultName) {
      setRunName(defaultName);
    }
  }, [visible, defaultName]);

  return (
    <Modal
      title="完成运行"
      open={visible}
      onCancel={onCancel}
      onOk={() => onConfirm(runName)}
      okText="完成"
      cancelText="取消"
    >
      <Form layout="vertical">
        <Form.Item label="运行名称">
          <Input value={runName} onChange={e => setRunName(e.target.value)} placeholder="输入运行名称" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// 编辑 Run 名称弹窗
// 库存行价格/渠道编辑器（每行独立状态，手动保存）
const InventoryRowEditor = ({ record, onSave }) => {
  const [meso, setMeso] = useState(record.mesoValue == null ? 0 : record.mesoValue);
  const [coin, setCoin] = useState(record.coinValue == null ? 0 : record.coinValue);
  const [sell, setSell] = useState(record.sellMethod || undefined);
  const [saving, setSaving] = useState(false);
  // 价格币种切换：'meso' 或 'coin'。默认根据已有值判断
  const [priceCurrency, setPriceCurrency] = useState(
    (record.coinValue && !record.mesoValue) ? 'coin' : 'meso'
  );

  // 当外部 record 更新（如刷新拉取）时，重置本地状态为服务端值
  useEffect(() => {
    setMeso(record.mesoValue == null ? 0 : record.mesoValue);
    setCoin(record.coinValue == null ? 0 : record.coinValue);
    setSell(record.sellMethod || undefined);
    setPriceCurrency((record.coinValue && !record.mesoValue) ? 'coin' : 'meso');
  }, [record]);

  const dirty = useMemo(() => {
    const origMeso = record.mesoValue == null ? 0 : record.mesoValue;
    const origCoin = record.coinValue == null ? 0 : record.coinValue;
    const origSell = record.sellMethod || undefined;
    return (meso || 0) !== origMeso || (coin || 0) !== origCoin || (sell || undefined) !== origSell;
  }, [meso, coin, sell, record]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(record.id, {
        mesoValue: meso || 0,
        coinValue: coin || 0,
        sellMethod: sell || ''
      });
    } finally {
      setSaving(false);
    }
  };

  // 切换币种时清零另一个（"二选一"语义）
  const handleCurrencyChange = (cur) => {
    setPriceCurrency(cur);
    if (cur === 'meso') setCoin(0);
    else setMeso(0);
  };

  const isMeso = priceCurrency === 'meso';
  const currentValue = isMeso ? meso : coin;
  const setCurrentValue = isMeso ? setMeso : setCoin;

  const currencySelector = (
    <Select
      size="small"
      value={priceCurrency}
      onChange={handleCurrencyChange}
      style={{ width: 70 }}
      getPopupContainer={() => document.body}
      options={[
        { value: 'meso', label: 'Meso' },
        { value: 'coin', label: 'Coin' },
      ]}
    />
  );

  return (
    <Space size="small" wrap={false}>
      <InputNumber
        size="small"
        min={0}
        precision={0}
        value={currentValue}
        onChange={(v) => setCurrentValue(v ?? 0)}
        style={{ width: 200 }}
        addonBefore={currencySelector}
        formatter={isMeso ? (x => `${x ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')) : undefined}
        parser={isMeso ? (x => x ? Number(`${x}`.replace(/,/g, '')) : 0) : undefined}
      />
      <Select
        size="small"
        value={sell}
        onChange={setSell}
        style={{ width: 100 }}
        placeholder="销售渠道"
        allowClear
        getPopupContainer={() => document.body}
        options={[
          { value: 'NPC', label: 'NPC' },
          { value: 'FM', label: 'FM' },
          { value: 'Others', label: 'Others' },
        ]}
      />
      <Button
        size="small"
        type={dirty ? 'primary' : 'default'}
        icon={<SaveOutlined />}
        onClick={handleSave}
        disabled={!dirty}
        loading={saving}
      >
        保存
      </Button>
    </Space>
  );
};

// 添加库存物品弹窗
const InventoryAddModal = ({ visible, onCancel, onConfirm, lootConfig, knownCharacters }) => {
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    if (visible) { setName(''); setQuantity(1); setParticipants([]); }
  }, [visible]);

  const itemOptions = useMemo(() => {
    return (lootConfig?.items || []).map(it => ({ value: it.name, label: it.name }));
  }, [lootConfig]);

  const charOptions = useMemo(() => {
    return (knownCharacters || []).map(c => ({ value: c, label: c }));
  }, [knownCharacters]);

  const handleOk = () => {
    if (!name.trim()) { message.warning('请输入物品名称'); return; }
    if (!quantity || quantity <= 0) { message.warning('数量必须大于 0'); return; }
    onConfirm(name.trim(), quantity, participants);
  };

  return (
    <Modal
      title={<><InboxOutlined /> 添加库存物品</>}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText="添加"
      cancelText="取消"
    >
      <Form layout="vertical">
        <Form.Item label="物品名称" required>
          <AutoComplete
            value={name}
            onChange={setName}
            options={itemOptions}
            filterOption={(input, option) => 
              (option?.value || '').toLowerCase().includes(input.toLowerCase())
            }
            placeholder="输入或选择物品名称"
            allowClear
          />
        </Form.Item>
        <Form.Item label="数量" required>
          <InputNumber 
            min={1} 
            value={quantity}
            onChange={v => setQuantity(v || 1)}
            style={{ width: '100%' }}
            precision={0}
          />
        </Form.Item>
        <Form.Item label="参与角色" extra="可从已知角色选择，也可直接输入新名字（回车确认，多选）">
          <Select
            mode="tags"
            value={participants}
            onChange={setParticipants}
            options={charOptions}
            placeholder="选择或输入参与角色"
            style={{ width: '100%' }}
            tokenSeparators={[',', ' ']}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const EditRunNameModal = ({ visible, onCancel, onSuccess, currentRun, currentUser }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && currentRun) {
      form.setFieldsValue({ name: currentRun.name });
    }
  }, [visible, currentRun, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const runName = (values.name || '').trim();
      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRun.id}?username=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: runName })
      });
      if (res.ok) {
        message.success('名称已更新');
        onSuccess(runName);
      } else {
        message.error('更新失败');
      }
    } catch (e) {
      message.error('更新失败');
    }
    setLoading(false);
  };

  return (
    <Modal
      title="编辑名称"
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={loading}
      okText="保存"
      cancelText="取消"
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="名称" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BossRush;
